import type { WidgetOptions, WidgetStylePreset, WidgetMessage, WidgetConfig, FashionTryOnModal, PendingConfirmation, WidgetActionEventStatus, WidgetActionEventDetail, WidgetAction, InsuranceIntakeResolution, ComparisonPublicView, ComparisonPublicItem, ComparisonPublicCriterion, ComparisonDerivedCriterion, ComparisonPublicEvidence, ComparisonEvidenceState, WidgetSource } from "./types";
import { recordActionTelemetry } from "./transport";
import { attr, escapeHtml, safeUrl, escapeAttribute, stringValue, parseRecord, recordArray, numberAttribute, statusLabel, publicVisitorText, positiveInteger, numberValue, booleanValue, stringArray, nonEmptyRecord, formatPrice, boundedActionAttribute, parseJsonAttribute } from "./formatting";

export function scriptShopperContext(script: HTMLScriptElement): Record<string, unknown> {
  const context: Record<string, unknown> = {
    source: attr(script, "data-shopper-context-source") || "storefront-widget"
  };
  const authenticated = booleanAttributeValue(attr(script, "data-shopper-authenticated"));
  if (authenticated !== null) {
    context.is_authenticated = authenticated;
  }
  const accountUrl = attr(script, "data-account-url");
  if (/^https?:\/\//.test(accountUrl)) {
    context.account_url = accountUrl;
  }
  const orderLookupUrl = attr(script, "data-order-lookup-url");
  if (/^https?:\/\//.test(orderLookupUrl)) {
    context.order_lookup_url = orderLookupUrl;
  }
  return context;
}

export function booleanAttributeValue(value: string): boolean | null {
  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "authenticated", "signed_in"].includes(normalized)) {
    return true;
  }
  if (["false", "0", "no", "anonymous", "guest"].includes(normalized)) {
    return false;
  }
  return null;
}

export function ensureSessionId(options: WidgetOptions): string {
  if (options.sessionId) {
    return options.sessionId;
  }
  try {
    const existing = window.localStorage.getItem(sessionStorageKey(options));
    if (existing) {
      return existing;
    }
    const next = nextSessionId();
    window.localStorage.setItem(sessionStorageKey(options), next);
    return next;
  } catch {
    return nextSessionId();
  }
}

export function scriptTheme(value: string): "auto" | "light" | "dark" {
  if (value === "dark" || value === "auto") {
    return value;
  }
  return "light";
}

export function widgetStylePreset(value: string): WidgetStylePreset {
  const normalized = value.trim().toLowerCase();
  if (normalized === "pioneer") {
    return "pioneer";
  }
  if (normalized === "sonic-insurance" || normalized === "insurance") {
    return "sonic-insurance";
  }
  if (["sotm", "soul-of-the-machine", "public-website"].includes(normalized)) {
    return "sotm";
  }
  if (["fashion", "sonic", "orchid", "commerce"].includes(normalized)) {
    return "fashion";
  }
  return "default";
}

export function nextSessionId(): string {
  return `widget-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function sessionStorageKey(options: WidgetOptions): string {
  const experienceVersion = options.stylePreset === "sotm" ? ":sotm-v5" : "";
  return `agent-platform:${options.customerId}:${options.appId}:session${experienceVersion}`;
}

export function transcriptStorageKey(options: WidgetOptions, sessionId: string): string {
  const experienceVersion = options.stylePreset === "sotm" ? ":sotm-v5" : "";
  return `agent-platform:${options.customerId}:${options.appId}:${sessionId}:messages${experienceVersion}`;
}

export function loadTranscript(options: WidgetOptions, sessionId: string): WidgetMessage[] {
  try {
    const raw = window.localStorage.getItem(transcriptStorageKey(options, sessionId));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as WidgetMessage[];
    return Array.isArray(parsed)
      ? parsed
          .filter((message) => typeof message?.content === "string" && typeof message?.role === "string")
          .map((message) => ({ ...message, sources: normalizeSources(message.sources), actions: normalizeActions(message.actions) }))
      : [];
  } catch {
    return [];
  }
}

export function saveTranscript(options: WidgetOptions, sessionId: string, messages: WidgetMessage[]) {
  try {
    window.localStorage.setItem(transcriptStorageKey(options, sessionId), JSON.stringify(messages.slice(-80)));
  } catch {
    // Transcript persistence is best-effort; the runtime remains the source of record.
  }
}

export function configPrimaryColor(config: WidgetConfig, fallback?: string): string | undefined {
  return (
    colorValue(config.colors?.buttonBackground) ||
    colorValue(config.colors?.primary) ||
    colorValue(config.colors?.accentColor) ||
    fallback
  );
}

export function insuranceMicIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 15.25a3.75 3.75 0 0 0 3.75-3.75V7a3.75 3.75 0 1 0-7.5 0v4.5A3.75 3.75 0 0 0 12 15.25Z" fill="currentColor"></path><path d="M6.5 11.25a.75.75 0 0 1 .75.75 4.75 4.75 0 0 0 9.5 0 .75.75 0 0 1 1.5 0 6.25 6.25 0 0 1-5.5 6.2v2.05h2.25a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1 0-1.5h2.25V18.2a6.25 6.25 0 0 1-5.5-6.2.75.75 0 0 1 .75-.75Z" fill="currentColor"></path></svg>`;
}

export function insuranceStopIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6.5" y="6.5" width="11" height="11" rx="2.5" fill="currentColor"></rect></svg>`;
}

export function insuranceResetIcon(): string {
  return `<span class="apw-insurance-button-icon" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M7.28 7.28A6.68 6.68 0 0 1 12 5.25a6.75 6.75 0 1 1-6.36 9 .75.75 0 1 1 1.41-.5 5.25 5.25 0 1 0 .99-5.37l1.31 1.31a.75.75 0 0 1-.53 1.28H4.75A.75.75 0 0 1 4 10.22V6.15a.75.75 0 0 1 1.28-.53l2 2Z" fill="currentColor"></path></svg></span>`;
}

export function insuranceCloseIcon(): string {
  return `<span class="apw-insurance-button-icon" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="m6.97 5.91 5.03 5.03 5.03-5.03a.75.75 0 1 1 1.06 1.06L13.06 12l5.03 5.03a.75.75 0 1 1-1.06 1.06L12 13.06l-5.03 5.03a.75.75 0 0 1-1.06-1.06L10.94 12 5.91 6.97a.75.75 0 0 1 1.06-1.06Z" fill="currentColor"></path></svg></span>`;
}

export function sotmResetIcon(): string {
  return `<svg class="apw-sotm-control-icon apw-sotm-refresh-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M15.25 7.5A6 6 0 1 0 16 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path><path d="M15.25 3.75V7.5H11.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
}

export function sotmCloseIcon(): string {
  return `<svg class="apw-sotm-control-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="m5.75 5.75 8.5 8.5m0-8.5-8.5 8.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>`;
}

export function sotmSendIcon(): string {
  return `<svg class="apw-sotm-send-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4.5 10h10.25m-4-4.25L15 10l-4.25 4.25" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
}

export function sotmSignalIcon(className = ""): string {
  const classAttribute = ["apw-sotm-signal", className].filter(Boolean).join(" ");
  return `<svg class="${classAttribute}" viewBox="0 0 40 40" aria-hidden="true" focusable="false"><path class="apw-sotm-signal-chevron" d="M6.695 35.625H5.349v-1.349H4.002v-1.35H2.656v-1.349h1.346v-1.35h1.347v-1.349h1.346v-1.35h1.346v-1.349h1.346V24.83h1.346v-1.35h1.346v-1.349h1.346v-2.699h-1.346v-1.35h-1.346v-1.349H9.387v-1.349H8.041v-1.35H6.695v-1.349H5.349v-1.35H4.002V9.986H2.656v-1.35h1.346V7.287h1.347V5.938h1.346v1.349h1.346v1.349h1.346v1.35h1.346v1.349h1.346v1.35h1.346v1.349h1.347v1.35h1.346v1.349h1.346v1.349h1.346v1.35h1.346v2.699H18.81v1.349h-1.346v1.35h-1.346v1.349h-1.346v1.349h-1.347v1.35h-1.346v1.349h-1.346v1.35H9.387v1.349H8.041v1.35H6.695v1.349Z"></path><path class="apw-sotm-signal-underscore" d="M20.156 35.577v-2.308h16.923v2.308Z"></path></svg>`;
}

export function insuranceBrandIcon(): string {
  return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.13a.5.5 0 0 1 .96 0l1.58 6.13a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96l-6.13 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path><path d="M20 3v4M22 5h-4M4 17v2M5 18H3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>`;
}

export function insuranceChoiceIcon(name = ""): string {
  const icons: Record<string, string> = {
    home: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.75 11.25 12 5.5l7.25 5.75v7a1.5 1.5 0 0 1-1.5 1.5h-3.5v-5.5h-4.5v5.5h-3.5a1.5 1.5 0 0 1-1.5-1.5v-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>`,
    townhouse: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.75 19.25V8.75l4-3 3.25 2.5 3.25-2.5 4 3v10.5H4.75Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M8.5 19.25v-5h3v5m4-8.25h.01M8.5 11h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    building: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7.25 19.25V5.75h9.5v13.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M9.75 8.75h.01m4.49 0h.01M9.75 12h.01m4.49 0h.01M9.75 15.25h.01m4.49 0h.01M5.5 19.25h13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    apartment: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5.75 19.25V7.25h12.5v12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M8.75 10.25h2m2.5 0h2m-6.5 3.25h2m2.5 0h2m-9.5 5.75h13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    multi_unit: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.75 19.25V8.75h6.5v10.5m1.5 0V5.75h6.5v13.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M7.25 11.25h1.5m-1.5 3h1.5m6.5-5.5h1.5m-1.5 3h1.5m-12 7.5h14.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    mobile_home: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.75 14.25h14.5v4.5H4.75v-4.5Zm2-4.5h10.5l2 4.5H4.75l2-4.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M8 18.75v1.5m8-1.5v1.5m-6.5-7.5h5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    key: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9.5 14.5a4 4 0 1 1 2.83-6.83 4 4 0 0 1-2.83 6.83Z" fill="none" stroke="currentColor" stroke-width="1.8"></path><path d="m12.25 12.25 7 7m-3.5-3.5-2 2m4-4-2 2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`,
    briefcase: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5.75 8.75h12.5a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H5.75a1.5 1.5 0 0 1-1.5-1.5v-7a1.5 1.5 0 0 1 1.5-1.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M9 8.75V6.5h6v2.25m-10.75 4h15.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    shield: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 4.75 18.25 7v4.75c0 3.65-2.5 6.55-6.25 7.5-3.75-.95-6.25-3.85-6.25-7.5V7L12 4.75Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="m9.25 12.25 1.75 1.75 3.75-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`,
    help: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7.25" fill="none" stroke="currentColor" stroke-width="1.8"></circle><path d="M9.9 9.75a2.2 2.2 0 1 1 3.55 1.73c-.82.64-1.45 1.09-1.45 2.02m0 2.75h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    scale: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5.25v14m-4.75-11h9.5M7.25 8.25 4.5 14h5.5L7.25 8.25Zm9.5 0L14 14h5.5l-2.75-5.75Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`,
    sparkles: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 4.75 13.7 9l4.55 1.25L13.7 11.5 12 15.75l-1.7-4.25-4.55-1.25L10.3 9 12 4.75Zm5.5 10.5.75 1.75 1.75.75-1.75.75-.75 1.75-.75-1.75-1.75-.75 1.75-.75.75-1.75Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>`,
    coin: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="7.25" fill="none" stroke="currentColor" stroke-width="1.8"></circle><path d="M12 7.75v8.5m2.25-6.25c-.35-.63-1.1-1-2.05-1-1.12 0-1.95.53-1.95 1.35 0 .78.62 1.1 2.05 1.45 1.35.33 2 .72 2 1.55 0 .87-.86 1.45-2.05 1.45-1.02 0-1.86-.39-2.3-1.05" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    calendar: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.75 5.75h10.5a1.5 1.5 0 0 1 1.5 1.5v10.5a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5V7.25a1.5 1.5 0 0 1 1.5-1.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M8.5 4.75v2m7-2v2m-10.25 4h13.5m-9.5 3h.01m3 0h.01m3 0h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path></svg>`,
    roof: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4.75 11.5 12 5.75l7.25 5.75" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="M6.5 10.25v8.5h11v-8.5M9.25 18.75v-5h5.5v5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>`,
    foundation: `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5.25 15.25h13.5l-1.25 4H6.5l-1.25-4Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path><path d="M7.25 15.25v-4.5L12 7.25l4.75 3.5v4.5M9 19.25v-4m3 4v-4m3 4v-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`
  };
  const normalized = name.toLowerCase().replace(/-/g, "_");
  return icons[normalized] || icons.shield;
}

export function renderWidgetTitle(config: WidgetConfig, stylePreset: WidgetStylePreset): string {
  if (stylePreset === "sonic-insurance") {
    return `<span class="apw-insurance-brand-mark" aria-hidden="true">${insuranceBrandIcon()}</span><span>Sonic Insurance</span>`;
  }
  if (stylePreset === "sotm") {
    return `<span class="apw-sotm-mark" aria-hidden="true">${sotmSignalIcon()}</span>`;
  }
  if (stylePreset !== "fashion") {
    return escapeHtml(config.title || "Agent chat");
  }
  const metadata = fashionMetadata(config);
  const logoUrl = fashionLogoUrl(config);
  const logoAlt = fashionLogoAlt(config);
  if (safeUrl(logoUrl)) {
    return `<img class="apw-brand-logo" src="${escapeAttribute(logoUrl)}" alt="${escapeAttribute(logoAlt)}">`;
  }
  return escapeHtml(stringValue(metadata.assistantName) || stringValue(metadata.assistant_name) || config.title || "Sonic");
}

export function renderFashionSettings(config: WidgetConfig): string {
  const metadata = fashionMetadata(config);
  const assistantName = stringValue(metadata.assistantName) || stringValue(metadata.assistant_name) || config.title || "Sonic";
  const logoUrl = fashionLogoUrl(config);
  const logoAlt = fashionLogoAlt(config);
  const prompts = fashionQuickReplies(metadata);
  const controls: Array<{ label: string; enabled: boolean }> = [
    { label: "Try-on", enabled: fashionBoolean(metadata, ["tryOnEnabled", "try_on_enabled"], true) },
    { label: "Price range", enabled: fashionBoolean(metadata, ["priceRangeEnabled", "price_range_enabled"], true) },
    { label: "Side rail", enabled: fashionBoolean(metadata, ["productRailEnabled", "product_rail_enabled"], true) },
    { label: "Compare", enabled: fashionBoolean(metadata, ["comparisonEnabled", "comparison_enabled"], true) }
  ];
  return `
    <div class="apw-fashion-settings-backdrop" data-fashion-settings-close="true"></div>
    <aside class="apw-fashion-settings-sheet" role="dialog" aria-label="Personalization and filters">
      <div class="apw-fashion-settings-head">
        <strong>Personalization</strong>
        <button type="button" class="apw-fashion-settings-close" data-fashion-settings-close="true" aria-label="Close personalization">x</button>
      </div>
      <div class="apw-fashion-settings-brand">
        <span>
          ${safeUrl(logoUrl) ? `<img src="${escapeAttribute(logoUrl)}" alt="${escapeAttribute(logoAlt)}">` : escapeHtml((assistantName || "S").slice(0, 1).toUpperCase())}
        </span>
        <div>
          <small>Store assistant</small>
          <b>${escapeHtml(assistantName)}</b>
          <em>${escapeHtml(logoAlt || "Brand identity")} configured in Agent Platform</em>
        </div>
      </div>
      <div class="apw-fashion-settings-controls" aria-label="Enabled shopping controls">
        ${controls
          .map(
            ({ label, enabled }) =>
              `<span data-enabled="${enabled ? "true" : "false"}"><i aria-hidden="true"></i>${escapeHtml(label)}</span>`
          )
          .join("")}
      </div>
      <p>${escapeHtml(assistantName)} can tune recommendations by sale depth, price, color, formality, availability, and fit context.</p>
      <div class="apw-fashion-settings-chips">
        ${prompts
          .map(
            (reply) =>
              `<button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(reply.prompt)}">${escapeHtml(reply.label)}</button>`
          )
          .join("")}
      </div>
    </aside>
  `;
}

export function fashionMetadata(config: WidgetConfig): Record<string, unknown> {
  const assets = parseRecord(config.assets);
  return {
    ...parseRecord(assets.metadata),
    ...parseRecord(config.metadata)
  };
}

export function fashionLogoUrl(config: WidgetConfig): string {
  const assets = parseRecord(config.assets);
  const metadata = fashionMetadata(config);
  return (
    stringValue(assets.logoUrl) ||
    stringValue(assets.logo_url) ||
    stringValue(assets.wordmarkUrl) ||
    stringValue(assets.wordmark_url) ||
    stringValue(metadata.logoUrl) ||
    stringValue(metadata.logo_url) ||
    stringValue(metadata.wordmarkUrl) ||
    stringValue(metadata.wordmark_url)
  );
}

export function fashionLogoAlt(config: WidgetConfig): string {
  const assets = parseRecord(config.assets);
  const metadata = fashionMetadata(config);
  return (
    stringValue(assets.logoAlt) ||
    stringValue(assets.logo_alt) ||
    stringValue(metadata.logoAlt) ||
    stringValue(metadata.logo_alt) ||
    stringValue(metadata.assistantName) ||
    stringValue(metadata.assistant_name) ||
    config.title ||
    "Store logo"
  );
}

export function fashionQuickReplies(metadata: Record<string, unknown>): Array<{ label: string; prompt: string }> {
  const configured = recordArray(metadata.quickReplies).length
    ? recordArray(metadata.quickReplies)
    : recordArray(metadata.quick_replies);
  const replies = configured
    .map((reply) => ({
      label: stringValue(reply.label),
      prompt: stringValue(reply.prompt)
    }))
    .filter((reply) => reply.label && reply.prompt)
    .slice(0, 8);
  return replies.length
    ? replies
    : [
        { label: "Best sale", prompt: "Show me the best sale items." },
        { label: "Cheaper", prompt: "Show cheaper options." },
        { label: "Different colors", prompt: "Show different colors." },
        { label: "More formal", prompt: "Make these options more formal." },
        { label: "Different brands", prompt: "Show different brands." },
        { label: "In stock", prompt: "Only show items that are in stock." }
      ];
}

export function fashionBoolean(metadata: Record<string, unknown>, keys: string[], fallback: boolean): boolean {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "string" && value.trim()) {
      return value.trim().toLowerCase() !== "false";
    }
  }
  return fallback;
}

export function renderFashionTryOnModal(modal: FashionTryOnModal): string {
  const preview = modal.previewUrl.startsWith("blob:") || safeUrl(modal.previewUrl) ? modal.previewUrl : "";
  const productImage = safeUrl(modal.imageUrl) ? modal.imageUrl : "";
  const generatedImages = modal.generatedImages.filter((imageUrl) => imageUrl.startsWith("data:image/") || safeUrl(imageUrl));
  return `
    <div class="apw-try-on-backdrop" data-try-on-close="true"></div>
    <aside class="apw-try-on-dialog" role="dialog" aria-modal="true" aria-label="Virtual try-on">
      <div class="apw-try-on-dialog-head">
        ${productImage ? `<img src="${escapeAttribute(productImage)}" alt="">` : `<span class="apw-product-card-thumb" aria-hidden="true"></span>`}
        <div>
          <small>Virtual try-on</small>
          <strong>${escapeHtml(modal.title || "Selected item")}</strong>
        </div>
        <button type="button" data-try-on-close="true" aria-label="Close virtual try-on">x</button>
      </div>
      <section class="apw-try-on-consent-copy" id="apw-try-on-consent-details" aria-label="Photo use and limitations">
        <strong>Before you choose a photo</strong>
        <ul>
          <li><b>Purpose:</b> your photo is sent to the store's configured try-on provider only to generate this preview.</li>
          <li><b>Retention:</b> Agent Platform does not persist the uploaded photo or generated preview; provider handling follows the store's privacy terms.</li>
          <li><b>Limitations:</b> the result is an AI visualization and does not guarantee fit, size, fabric behavior, or availability.</li>
        </ul>
      </section>
      <label class="apw-try-on-consent">
        <input class="apw-try-on-consent" type="checkbox" aria-describedby="apw-try-on-consent-details" ${modal.consentConfirmed ? "checked" : ""} ${modal.isGenerating ? "disabled" : ""}>
        <span>I understand and consent to this photo use for one try-on request.</span>
      </label>
      <label class="apw-try-on-upload-drop">
        <span>${preview ? "Replace photo" : "Upload a full-body photo or selfie"}</span>
        <input class="apw-try-on-upload" type="file" accept="image/png,image/jpeg,image/webp" ${modal.consentConfirmed && !modal.isGenerating ? "" : "disabled"} aria-disabled="${modal.consentConfirmed && !modal.isGenerating ? "false" : "true"}">
      </label>
      ${
        preview
          ? `<figure class="apw-try-on-preview"><img src="${escapeAttribute(preview)}" alt=""><figcaption>${escapeHtml(modal.fileName || "Uploaded photo")}</figcaption></figure>`
          : ""
      }
      ${
        modal.status
          ? `<div class="apw-try-on-status" role="${modal.error ? "alert" : "status"}" aria-live="${modal.error ? "assertive" : "polite"}" data-error="${modal.error ? "true" : "false"}" data-busy="${modal.isGenerating ? "true" : "false"}">${escapeHtml(modal.status)}</div>`
          : ""
      }
      ${
        generatedImages.length
          ? `<div class="apw-try-on-results">${generatedImages
              .map((imageUrl) => `<img src="${escapeAttribute(imageUrl)}" alt="Generated try-on preview">`)
              .join("")}</div>`
          : ""
      }
      <div class="apw-try-on-dialog-actions">
        <button type="button" data-try-on-close="true">Cancel</button>
        ${
          safeUrl(modal.productUrl)
            ? `<a class="apw-inline-cta apw-action-open" href="${escapeAttribute(modal.productUrl)}" target="_blank" rel="noreferrer">View product</a>`
            : ""
        }
        <button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(modal.prompt || `Help me style ${modal.title}.`)}">Ask for styling help</button>
      </div>
    </aside>
  `;
}

export function pendingConfirmationFromElement(
  element: HTMLElement,
  options: WidgetOptions,
  sessionId: string
): PendingConfirmation {
  const label =
    element.getAttribute("data-label") ||
    element.querySelector("strong")?.textContent?.trim() ||
    element.textContent?.trim() ||
    "Confirm";
  return {
    status: "prompted",
    appId: options.appId,
    customerId: options.customerId,
    sessionId,
    actionId: element.getAttribute("data-action-id") || "",
    actionType: element.getAttribute("data-action-type") || "",
    destinationKind: element.getAttribute("data-destination-kind") || "",
    url: element.getAttribute("data-url") || "",
    valueMicros: numberAttribute(element, "data-value-micros") ?? undefined,
    currency: element.getAttribute("data-currency") || undefined,
    metadata: actionElementMetadata(element),
    label,
    message: element.getAttribute("data-confirm") || "Confirm before continuing.",
    confirmLabel: element.getAttribute("data-confirm-label") || "Confirm",
    cancelLabel: element.getAttribute("data-cancel-label") || "Cancel",
    prompt: element.getAttribute("data-prompt") || undefined,
    displayContent: element.getAttribute("data-display-message") || undefined,
    personalizationMutation: ["preference_update", "preference_forget", "preference_opt_out"].includes(
      element.getAttribute("data-action-type") || ""
    )
      ? {
          message: element.getAttribute("data-preference-message") || "",
          category: element.getAttribute("data-preference-category") || "",
          value: element.getAttribute("data-preference-value") || "",
          preferenceId: element.getAttribute("data-preference-id") || "",
          forgetAll: element.getAttribute("data-preference-forget-all") === "true"
        }
      : undefined
  };
}

export function pendingActionEvent(
  pending: PendingConfirmation,
  status: WidgetActionEventStatus
): WidgetActionEventDetail {
  return {
    status,
    appId: pending.appId,
    customerId: pending.customerId,
    sessionId: pending.sessionId,
    actionId: pending.actionId,
    actionType: pending.actionType,
    destinationKind: pending.destinationKind,
    url: pending.url,
    valueMicros: pending.valueMicros,
    currency: pending.currency,
    metadata: pending.metadata
  };
}

export function renderPendingConfirmation(confirmation: PendingConfirmation): string {
  return `
    <aside class="apw-confirmation" role="dialog" aria-modal="false" aria-label="Required confirmation">
      <div>
        <span>Confirmation required</span>
        <strong>${escapeHtml(confirmation.label || "Confirm action")}</strong>
        <p>${escapeHtml(confirmation.message || "Confirm before continuing.")}</p>
        ${confirmation.destinationKind ? `<small>${escapeHtml(statusLabel(confirmation.destinationKind))}</small>` : ""}
      </div>
      <div class="apw-confirmation-actions">
        <button type="button" data-confirmation-cancel>${escapeHtml(confirmation.cancelLabel || "Cancel")}</button>
        <button type="button" data-confirmation-confirm>${escapeHtml(confirmation.confirmLabel || "Confirm")}</button>
      </div>
    </aside>
  `;
}

export function colorValue(value: unknown): string | undefined {
  return typeof value === "string" && /^#[0-9a-f]{3,8}$/i.test(value.trim()) ? value.trim() : undefined;
}

export function dispatchActionEvent(
  element: HTMLElement,
  status: WidgetActionEventStatus,
  options: WidgetOptions,
  sessionId: string
) {
  const detail: WidgetActionEventDetail = {
    status,
    appId: options.appId,
    customerId: options.customerId,
    sessionId,
    actionId: element.getAttribute("data-action-id") || "",
    actionType: element.getAttribute("data-action-type") || "",
    destinationKind: element.getAttribute("data-destination-kind") || "",
    url: element.getAttribute("data-url") || "",
    valueMicros: numberAttribute(element, "data-value-micros") ?? undefined,
    currency: element.getAttribute("data-currency") || undefined,
    metadata: actionElementMetadata(element)
  };
  dispatchActionDetail(detail, options);
}

export function dispatchActionDetail(detail: WidgetActionEventDetail, options: WidgetOptions) {
  window.dispatchEvent(
    new CustomEvent("agent-platform:action", {
      detail
    })
  );
  recordActionTelemetry(detail, options);
}

export function renderMessageContent(value: string): string {
  const blocks: string[] = [];
  const lines = normalizeInlineBulletMarkdown(publicVisitorText(value)).replace(/\r\n/g, "\n").split("\n");
  let listItems: string[] = [];
  const flushList = () => {
    if (!listItems.length) {
      return;
    }
    blocks.push(`<ul>${listItems.map((item) => `<li>${inlineMarkdown(item)}</li>`).join("")}</ul>`);
    listItems = [];
  };
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }
    const bullet = trimmed.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      listItems.push(bullet[1] ?? "");
      continue;
    }
    flushList();
    blocks.push(`<p>${inlineMarkdown(trimmed)}</p>`);
  }
  flushList();
  return blocks.join("") || "<p></p>";
}

export function normalizeInlineBulletMarkdown(value: string): string {
  const normalized = value.replace(/\r\n/g, "\n");
  const inlineBullets = normalized.match(/\s\*\s+(?=\*\*[^*]+\*\*)/g) ?? [];
  if (inlineBullets.length < 2) {
    return normalized;
  }
  return normalized.replace(/\s\*\s+(?=\*\*[^*]+\*\*)/g, "\n* ");
}

export function renderWidgetMessage(
  message: WidgetMessage,
  stylePreset: WidgetStylePreset,
  context: { historicalInsuranceRich?: boolean; nextUserContent?: string } = {}
): string {
  const actions = normalizeActions(message.actions);
  if (context.historicalInsuranceRich) {
    return renderInsuranceHistory(actions, context.nextUserContent || "");
  }
  const insurancePresentation = stylePreset === "sonic-insurance";
  const insuranceRichPresentation =
    insurancePresentation &&
    message.role === "assistant" &&
    actions.some((action) => ["intake_form", "coverage_card"].includes(action.type));
  const commercePresentation =
    stylePreset === "fashion" && message.role === "assistant" && actions.some((action) => isFashionRichAction(action));
  const displayContent =
    insurancePresentation && message.role === "user" ? insuranceDisplayMessage(message.content) : message.content;
  const sotmAssistant = stylePreset === "sotm" && message.role === "assistant";
  return `
    <div class="apw-message" data-role="${message.role}" data-protocol-version="${escapeAttribute(message.protocolVersion || "2")}" ${commercePresentation ? `data-commerce="true"` : ""} ${insuranceRichPresentation ? `data-insurance-rich="true"` : ""}>
      ${commercePresentation || insurancePresentation ? "" : `<span class="apw-role-label">${sotmAssistant ? `${sotmSignalIcon("apw-sotm-role-signal")} SOTM` : escapeHtml(message.role)}</span>`}
      <div class="apw-message-content">${
        insuranceRichPresentation ? "" : commercePresentation ? renderCommerceMessageContent(message, actions) : renderMessageContent(displayContent)
      }</div>
      ${insurancePresentation || stylePreset === "sotm" ? "" : renderProtocolStrip(message)}
      ${insurancePresentation || stylePreset === "sotm" ? "" : renderVisualSummary(message.visualSummary)}
      ${insurancePresentation || stylePreset === "sotm" ? "" : renderRequiredConfirmation(message.requiredConfirmation)}
      ${renderActions(actions, stylePreset)}
      ${insurancePresentation || stylePreset === "sotm" ? "" : renderSources(message.sources)}
    </div>
  `;
}

export function renderInsuranceWelcome(): string {
  const replies = [
    { label: "Get a home quote", prompt: "Help me start a home insurance quote", tone: "blue" },
    { label: "Compare coverage", prompt: "Help me compare home insurance coverage", tone: "violet" },
    { label: "Explain discounts", prompt: "Explain common home insurance discounts", tone: "green" }
  ];
  return `<div class="apw-insurance-welcome">
    <div class="apw-insurance-greeting">Hi! I'm Scout. I can help you explore home insurance, compare coverage, and walk through an illustrative quote.</div>
    <div class="apw-insurance-starters">${replies
      .map(
        (reply) =>
          `<button class="apw-quick-reply" data-tone="${reply.tone}" type="button" data-prompt="${escapeAttribute(reply.prompt)}">${escapeHtml(reply.label)}</button>`
      )
      .join("")}</div>
  </div>`;
}

export function renderSotmWelcome(config: WidgetConfig): string {
  const metadata = parseRecord(config.metadata);
  const configured = recordArray(metadata.quickReplies).length
    ? recordArray(metadata.quickReplies)
    : recordArray(metadata.quick_replies);
  const replies = configured
    .map((reply) => ({ label: stringValue(reply.label), prompt: stringValue(reply.prompt) }))
    .filter((reply) => reply.label && reply.prompt)
    .slice(0, 5);
  const starters = replies.length
    ? replies
    : [
        { label: "What does SOTM do?", prompt: "What does Soul of the Machine do?" },
        { label: "Meet the founders", prompt: "Who founded and leads Soul of the Machine?" },
        { label: "Recent insights", prompt: "Show me the most recent insights." },
        { label: "Explore careers", prompt: "What roles are currently open?" },
        { label: "Let's talk", prompt: "How can I contact Soul of the Machine?" }
      ];
  return `<div class="apw-sotm-welcome">
    <h2>What can we help you explore?</h2>
    <p>Ask about our approach, team, thinking, open roles, or starting a project.</p>
    <div class="apw-sotm-starters">${starters
      .map(
        (reply) =>
          `<button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(reply.prompt)}"><span>${escapeHtml(reply.label)}</span><b aria-hidden="true">&#8599;</b></button>`
      )
      .join("")}</div>
  </div>`;
}

export function insuranceLocationDisplay(value: string): string {
  const normalized = value.trim().replace(/^the property is located in\s+/i, "").replace(/\.$/, "");
  if (/^94582$/i.test(normalized)) {
    return "San Ramon, CA 94582";
  }
  return normalized;
}

export function insuranceDisplayMessage(content: string): string {
  const normalized = content.trim();
  const exactLabels: Record<string, string> = {
    "I own and live in the property.": "I own and live there",
    "I own a condo.": "I own a condo",
    "I rent the place.": "I rent",
    "I own the property and rent it to others.": "I rent it to others",
    "I only need earthquake protection.": "Earthquake only",
    "I'm not sure which property relationship applies.": "I'm not sure",
    "It is a single-family house.": "Single-family house",
    "It is a townhouse.": "Townhouse",
    "It is a condo.": "Condo",
    "It is an apartment.": "Apartment",
    "It is a multi-unit property.": "Multi-unit",
    "It is a manufactured or mobile home.": "Manufactured home",
    "The home was built in 2020 or later.": "2020 or newer",
    "The home was built between 2000 and 2019.": "2000-2019",
    "The home was built between 1980 and 1999.": "1980-1999",
    "The home was built before 1980.": "Before 1980",
    "I am not sure when the home was built.": "I'm not sure",
    "The roof is new or recently replaced.": "New roof",
    "The roof is 1 to 5 years old.": "1-5 years",
    "The roof is 6 to 15 years old.": "6-15 years",
    "The roof is 16 or more years old.": "16+ years",
    "I am not sure how old the roof is.": "I'm not sure",
    "The home has a slab foundation.": "Slab",
    "The home has a crawlspace foundation.": "Crawlspace",
    "The home has a basement foundation.": "Basement",
    "The home has a raised or pier foundation.": "Raised / pier",
    "I am not sure what type of foundation the home has.": "I'm not sure",
    "I want Essential coverage.": "Essential",
    "I want Balanced coverage.": "Balanced",
    "I want Enhanced coverage.": "Enhanced",
    "I prefer a $500 deductible.": "$500",
    "I prefer a $1,000 deductible.": "$1,000",
    "I prefer a $2,500 deductible.": "$2,500",
    "I prefer a $5,000 deductible.": "$5,000"
  };
  if (exactLabels[normalized]) {
    return exactLabels[normalized];
  }
  if (/^the property is located in\s+/i.test(normalized)) {
    return insuranceLocationDisplay(normalized);
  }
  return normalized;
}

export function resolveInsuranceIntakeInput(action: WidgetAction, content: string): InsuranceIntakeResolution {
  const input = content.trim();
  const layout = stringValue(action.data.layout);
  if (layout === "location_input") {
    const location = insuranceLocationAnswer(input);
    if (location) {
      return {
        kind: "resolved",
        prompt: `The property is located in ${location}.`,
        displayContent: insuranceLocationDisplay(location)
      };
    }
    return { kind: "ambiguous", message: insuranceIntakeClarification(action, input) };
  }

  const options = recordArray(action.data.options);
  const normalizedInput = normalizeInsuranceChoice(input);
  const indexedOption = insuranceIndexedOption(normalizedInput, options);
  if (indexedOption) {
    return resolvedInsuranceOption(indexedOption);
  }

  const field = stringValue(action.data.field);
  const exactOption = options.find((option) =>
    insuranceOptionCandidates(field, option).includes(normalizedInput)
  );
  if (exactOption) {
    return resolvedInsuranceOption(exactOption);
  }

  const fuzzyOption = insuranceFuzzyOption(field, normalizedInput, options);
  if (fuzzyOption) {
    return resolvedInsuranceOption(fuzzyOption);
  }

  if (/^(?:i am |i'm )?(?:not sure|unsure|dont know|don't know|do not know)$/.test(input.toLowerCase())) {
    const uncertainOption = options.find((option) => {
      const value = stringValue(option.value).toLowerCase();
      const label = stringValue(option.label).toLowerCase();
      return value === "unknown" || value === "unsure" || label.includes("not sure");
    });
    if (uncertainOption) {
      return resolvedInsuranceOption(uncertainOption);
    }
  }

  return { kind: "ambiguous", message: insuranceIntakeClarification(action, input) };
}

export function resolvedInsuranceOption(option: Record<string, unknown>): InsuranceIntakeResolution {
  const label = stringValue(option.label) || stringValue(option.value);
  return {
    kind: "resolved",
    prompt: stringValue(option.prompt) || stringValue(option.message) || label,
    displayContent: label
  };
}

export function insuranceIndexedOption(
  normalizedInput: string,
  options: Array<Record<string, unknown>>
): Record<string, unknown> | null {
  const indexWords: Record<string, number> = {
    one: 1,
    first: 1,
    two: 2,
    second: 2,
    three: 3,
    third: 3,
    four: 4,
    fourth: 4,
    five: 5,
    fifth: 5,
    six: 6,
    sixth: 6,
    seven: 7,
    seventh: 7,
    eight: 8,
    eighth: 8
  };
  const match = normalizedInput.match(/^(?:(?:option|number) )?(\d|one|first|two|second|three|third|four|fourth|five|fifth|six|sixth|seven|seventh|eight|eighth)(?: option)?$/);
  if (!match) {
    return null;
  }
  const index = /^\d$/.test(match[1] || "") ? Number(match[1]) : indexWords[match[1] || ""];
  return index && index <= options.length ? options[index - 1] ?? null : null;
}

export function insuranceChoiceAliases(field: string, value: string): string[] {
  const aliases: Record<string, Record<string, string[]>> = {
    ownership_status: {
      own_live: [
        "owner occupied",
        "primary residence",
        "i live there",
        "i own my home",
        "homeowner",
        "i own and live there"
      ],
      condo_owner: ["condo owner", "my condo", "i own the condo"],
      // Gemini Live has repeatedly transcribed "I rent" as "IRand" in this
      // constrained choice turn. These aliases are deliberately scoped to
      // ownership_status so they cannot affect free-form chat or other fields.
      rent: ["renter", "renters", "tenant", "i am renting", "i rand", "irand", "i ran", "i rant"],
      landlord: ["landlord", "rent it out", "rental property", "i rent the property out"],
      earthquake_only: ["earthquake", "earthquake coverage", "just earthquake"],
      unsure: ["help me choose", "not sure", "unsure", "i dont know"]
    },
    property_type: {
      single_family: ["single family", "single family home", "detached home", "house"],
      // "Jain House" is an observed final transcript for "townhouse". Keep
      // phonetic corrections field-local and require a complete phrase match.
      townhouse: ["townhome", "town house", "jain house", "chain house", "tan house"],
      condo: ["condominium", "con dough"],
      apartment: ["flat", "a part mint"],
      multi_unit: ["multi unit", "multi you knit", "duplex", "triplex"],
      manufactured: ["mobile home", "manufactured", "manufactured house", "modular home"]
    },
    home_age_range: {
      "2020": ["2020", "twenty twenty", "new construction"],
      "2000_2019": ["2000 to 2019", "two thousand to twenty nineteen"],
      "1980_1999": ["1980 to 1999", "nineteen eighty to nineteen ninety nine"],
      pre_1980: ["older than 1980", "pre 1980"]
    },
    roof_age_range: {
      new: ["new", "recently replaced"],
      "1_5": ["one to five", "one through five"],
      "6_15": ["six to fifteen", "six through fifteen"],
      "16_plus": ["sixteen plus", "more than sixteen"]
    },
    foundation_type: {
      slab: ["concrete slab"],
      crawlspace: ["crawl space"],
      basement: ["below grade"],
      raised_pier: ["raised pier", "pier and beam"]
    },
    coverage_preference: {
      Essential: ["basic", "essential plan"],
      Balanced: ["standard", "balanced plan"],
      Enhanced: ["premium", "enhanced plan"]
    }
  };
  return aliases[field]?.[value] ?? aliases[field]?.[value.toLowerCase()] ?? [];
}

export function insuranceOptionCandidates(field: string, option: Record<string, unknown>): string[] {
  return [
    option.label,
    option.prompt,
    option.message,
    option.value,
    ...insuranceChoiceAliases(field, stringValue(option.value))
  ]
    .map((candidate) => normalizeInsuranceChoice(stringValue(candidate)))
    .filter(Boolean);
}

export function insuranceFuzzyOption(
  field: string,
  normalizedInput: string,
  options: Array<Record<string, unknown>>
): Record<string, unknown> | null {
  const compactInput = normalizedInput.replace(/\s+/g, "");
  if (compactInput.length < 4) {
    return null;
  }

  const scores = options
    .map((option) => ({
      option,
      score: Math.max(
        0,
        ...insuranceOptionCandidates(field, option).map((candidate) =>
          insuranceChoiceSimilarity(compactInput, candidate.replace(/\s+/g, ""))
        )
      )
    }))
    .sort((left, right) => right.score - left.score);
  const best = scores[0];
  const runnerUp = scores[1];
  if (!best) {
    return null;
  }

  const minimumScore = compactInput.length <= 5 ? 0.8 : compactInput.length <= 8 ? 0.75 : 0.78;
  const hasSafeMargin = !runnerUp || best.score === 1 || best.score - runnerUp.score >= 0.12;
  return best.score >= minimumScore && hasSafeMargin ? best.option : null;
}

export function insuranceChoiceSimilarity(left: string, right: string): number {
  if (!left || !right) {
    return 0;
  }
  if (left === right) {
    return 1;
  }
  const longest = Math.max(left.length, right.length);
  const allowedDistance = Math.max(1, Math.floor(longest * 0.25));
  if (Math.abs(left.length - right.length) > allowedDistance) {
    return 0;
  }
  const distance = insuranceEditDistance(left, right, allowedDistance);
  return distance > allowedDistance ? 0 : 1 - distance / longest;
}

export function insuranceEditDistance(left: string, right: string, limit: number): number {
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    let rowMinimum = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const substitution = previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1);
      const value = Math.min(previous[rightIndex] + 1, current[rightIndex - 1] + 1, substitution);
      current.push(value);
      rowMinimum = Math.min(rowMinimum, value);
    }
    if (rowMinimum > limit) {
      return limit + 1;
    }
    previous = current;
  }
  return previous[right.length] ?? limit + 1;
}

export function insuranceLocationAnswer(content: string): string {
  const answer = content
    .trim()
    .replace(/^(?:the )?property (?:is )?(?:located )?(?:in|at)\s+/i, "")
    .replace(/^(?:i (?:am|live)|we (?:are|live)|its|it's)\s+(?:in|at)\s+/i, "")
    .replace(/[.!?]+$/, "")
    .trim();
  if (!answer || /^\d{1,4}$/.test(answer)) {
    return "";
  }
  return answer;
}

export function normalizeInsuranceChoice(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/&/g, " and ")
    .replace(/[$,]/g, "")
    .replace(/[_/+-]/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function insuranceIntakeClarification(action: WidgetAction, input: string): string {
  const options = recordArray(action.data.options);
  const labels = options.map((option) => stringValue(option.label)).filter(Boolean);
  const heard = input ? `I heard “${input},” but ` : "";
  if (!labels.length) {
    return `${heard}I need a ZIP code, city and state, or state before we continue.`;
  }
  const choiceList = labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
  return `${heard}I didn't catch which choice you meant. Please choose or say: ${choiceList}.`;
}

export function renderInsuranceHistory(actions: WidgetAction[], nextUserContent: string): string {
  const action = actions.find((candidate) => candidate.type === "intake_form");
  if (!action) return "";
  const options = recordArray(action.data.options);
  const normalizedNext = nextUserContent.trim().toLowerCase();
  const selected = options.find((option) => {
    const prompt = stringValue(option.prompt).trim().toLowerCase();
    const label = stringValue(option.label).trim().toLowerCase();
    return (prompt && (prompt === normalizedNext || normalizedNext.includes(prompt))) || (label && label === normalizedNext);
  });
  const selectedLabel =
    stringValue(selected?.label) ||
    nextUserContent.replace(/^the property is located in\s+/i, "").replace(/\.$/, "");
  return `<div class="apw-message apw-insurance-history" data-role="assistant" title="${escapeAttribute(stringValue(action.data.title) || "Quote detail")}">
    <span class="apw-insurance-history-label">${escapeHtml(stringValue(action.data.eyebrow) || "Quote detail")}</span>
    ${selectedLabel ? `<span class="apw-insurance-history-answer">${escapeHtml(selectedLabel)}</span>` : ""}
  </div>`;
}

export function renderTypingIndicator(voiceActive: boolean, stylePreset: WidgetStylePreset = "default"): string {
  const status = voiceActive ? "Preparing answer" : "Assistant is typing";
  const sotm = stylePreset === "sotm";
  return `
    <div class="apw-message apw-typing-message" data-role="assistant" data-pending="true" role="status" aria-live="polite" aria-label="${escapeHtml(status)}">
      <span class="apw-role-label">${sotm ? `${sotmSignalIcon("apw-sotm-role-signal")} FINDING THE BEST ANSWER` : "assistant"}</span>
      <div class="apw-message-content">
        <span class="apw-typing-dots" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
        <span class="apw-sr-only">${escapeHtml(status)}</span>
      </div>
    </div>
  `;
}

export function renderCommerceMessageContent(message: WidgetMessage, actions: WidgetAction[]): string {
  const comparison = actions.find((action) => action.type === "comparison" || action.type === "comparison_carousel");
  if (comparison) {
    const itemCount = recordArray(comparison.data.items).slice(0, 4).length;
    const summary = itemCount > 0
      ? `I compared ${itemCount} displayed option${itemCount === 1 ? "" : "s"} using your requested criteria. Review the evidence status and criterion-specific outcomes below.`
      : "I could not verify enough displayed options for this comparison.";
    return `<p>${escapeHtml(summary)}</p>`;
  }
  const grid = actions.find((action) => action.type === "product_grid");
  if (!grid) {
    return renderMessageContent(message.content);
  }
  const items = recordArray(grid?.data.items);
  const resultCount = positiveInteger(grid?.data.resultCount) ?? items.length;
  const query = stringValue(grid?.data.query);
  const filterLabel = commerceFilterLabel(query);
  const summary = resultCount > 0
    ? `I found ${resultCount} item${resultCount === 1 ? "" : "s"} that match your request.`
    : stringValue(grid?.data.summary) || message.content;
  return `
    ${filterLabel ? `<div class="apw-active-filters"><span>${escapeHtml(filterLabel)} <b>x</b></span></div>` : ""}
    <p>${escapeHtml(summary)}</p>
  `;
}

export function commerceFilterLabel(query: string): string {
  const normalized = query.toLowerCase();
  if (normalized.includes("sale") || normalized.includes("discount")) {
    return "Sale";
  }
  if (normalized.includes("formal")) {
    return "Formal";
  }
  if (normalized.includes("color") || normalized.includes("colour")) {
    return "Color";
  }
  return "";
}

export function inlineMarkdown(value: string): string {
  return escapeHtml(value).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

export function renderProtocolStrip(message: WidgetMessage): string {
  if (message.role !== "assistant") {
    return "";
  }
  const trust = message.trust && Object.keys(message.trust).length ? message.trust : {};
  const confirmation =
    message.requiredConfirmation && Object.keys(message.requiredConfirmation).length ? message.requiredConfirmation : {};
  const confidence = numberValue(trust.confidence);
  const protocolLabel = (message.protocolVersion || "2").toUpperCase();
  const confidenceLabel = confidence === null ? "" : `${Math.round(confidence * 100)}% grounded`;
  const sourceCount = numberValue(trust.sourceCount);
  const sourceLabel = sourceCount === null || sourceCount <= 0 ? "" : `${sourceCount} sources`;
  const confirmationLabel = stringValue(confirmation.reason) || stringValue(confirmation.label);
  if (!protocolLabel && !confidenceLabel && !sourceLabel && !confirmationLabel) {
    return "";
  }
  return `
    <div class="apw-protocol-strip" aria-label="Response status">
      ${protocolLabel ? `<span>${escapeHtml(protocolLabel)}</span>` : ""}
      ${confidenceLabel ? `<span>${escapeHtml(confidenceLabel)}</span>` : ""}
      ${sourceLabel ? `<span>${escapeHtml(sourceLabel)}</span>` : ""}
      ${confirmationLabel ? `<span>${escapeHtml(confirmationLabel)}</span>` : ""}
    </div>
  `;
}

export function renderVisualSummary(value?: Record<string, unknown> | null): string {
  const summary = parseRecord(value);
  const title = stringValue(summary.title);
  const actionTypes = Array.isArray(summary.actionTypes) ? summary.actionTypes.map((item) => stringValue(item)).filter(Boolean) : [];
  if (!title && !actionTypes.length) {
    return "";
  }
  return `
    <div class="apw-visual-summary" aria-label="Visual summary">
      ${title ? `<strong>${escapeHtml(title)}</strong>` : ""}
      ${actionTypes.length ? `<small>${escapeHtml(actionTypes.slice(0, 4).map(statusLabel).join(" / "))}</small>` : ""}
    </div>
  `;
}

export function renderRequiredConfirmation(value?: Record<string, unknown> | null): string {
  const confirmation = parseRecord(value);
  const label = stringValue(confirmation.label);
  const reason = stringValue(confirmation.reason);
  if (!label && !reason) {
    return "";
  }
  return `
    <div class="apw-required-confirmation" aria-label="Required confirmation">
      <strong>${escapeHtml(label || "Confirmation required")}</strong>
      ${reason ? `<small>${escapeHtml(reason)}</small>` : ""}
    </div>
  `;
}

export function renderActions(actions?: WidgetAction[], stylePreset: WidgetStylePreset = "default"): string {
  const normalizedActions = normalizeActions(actions);
  const insuranceActionTypes = new Set([
    "intake_form",
    "coverage_card",
    "quick_reply",
    "refinement_chip",
    "support_handoff",
    "handoff_status",
    "contact_support"
  ]);
  const sotmActionTypes = new Set([
    "article_grid",
    "article_card",
    "case_study_grid",
    "case_study_card",
    "person_card",
    "service_card",
    "job_card",
    "navigation_card",
    "cta_card",
    "quick_reply"
  ]);
  const safeActions =
    stylePreset === "sonic-insurance"
      ? normalizedActions.filter((action) => insuranceActionTypes.has(action.type)).slice(0, 3)
    : stylePreset === "sotm"
      ? normalizedActions.filter((action) => sotmActionTypes.has(action.type)).slice(0, 8)
    : stylePreset === "fashion"
      ? selectFashionActions(normalizedActions)
      : normalizedActions.slice(0, 12);
  if (!safeActions.length) {
    return "";
  }
  return `
    <div class="apw-actions" aria-label="Assistant actions">
      ${safeActions.map((action) => renderAction(action)).join("")}
    </div>
  `;
}

export function selectFashionActions(actions: WidgetAction[]): WidgetAction[] {
  const supported = actions.filter(
    (action) => sonicFashionActionTypes.has(action.type) || sonicFashionLegacyActionTypes.has(action.type)
  );
  const selected = supported.slice(0, 12);
  const selectedKeys = new Set(selected.map((action) => `${action.type}:${action.id}`));
  // The visual action cap protects the transcript from unbounded product and
  // refinement payloads. State, recovery, authentication, and handoff actions
  // remain visible even when a producer sends them after that cap.
  for (const action of supported.slice(12)) {
    const key = `${action.type}:${action.id}`;
    if (sonicFashionPreservedActionTypes.has(action.type) && !selectedKeys.has(key)) {
      selected.push(action);
      selectedKeys.add(key);
    }
  }
  return selected;
}

export function isFashionRichAction(action: WidgetAction): boolean {
  return [
    "product_grid",
    "product_card",
    "recommendation",
    "comparison",
    "comparison_carousel",
    "try_on",
    "try_on_result",
    "weather_card",
    "weather_context",
    "contact_module",
    "contact_capture"
  ].includes(action.type);
}

export function renderAction(action: WidgetAction): string {
  if (action.type === "article_grid") {
    return renderContentGrid(action, "article");
  }
  if (action.type === "article_card") {
    return renderContentCard(action, "article");
  }
  if (action.type === "case_study_grid") {
    return renderContentGrid(action, "case_study");
  }
  if (action.type === "case_study_card") {
    return renderContentCard(action, "case_study");
  }
  if (action.type === "person_card") {
    return renderContentCard(action, "person");
  }
  if (action.type === "service_card") {
    return renderContentCard(action, "service");
  }
  if (action.type === "job_card") {
    return renderContentCard(action, "job");
  }
  if (["navigation_card", "cta_card"].includes(action.type)) {
    return renderContentCard(action, "navigation");
  }
  if (action.type === "source_list") {
    return renderCitationList(action);
  }
  if (action.type === "product_grid") {
    return renderProductGrid(action);
  }
  if (action.type === "product_card") {
    return renderProductCard(action);
  }
  if (action.type === "product_detail_link") {
    return renderActionLink(action, "apw-product-detail-action");
  }
  if (["refinement_chip", "remove_refinement", "clear_refinements"].includes(action.type)) {
    return renderPromptAction(action);
  }
  if (action.type === "price_range") {
    return renderPriceRangeAction(action);
  }
  if (action.type === "comparison" || action.type === "comparison_carousel") {
    return renderComparison(action);
  }
  if (action.type === "comparison_page") {
    return renderActionLink(action, "apw-comparison-page-action");
  }
  if (action.type === "recommendation") {
    return renderRecommendation(action);
  }
  if (action.type === "weather_card" || action.type === "weather_context") {
    return renderWeatherCard(action);
  }
  if (action.type === "contact_module" || action.type === "contact_capture") {
    return renderContactModule(action);
  }
  if (action.type === "add_to_cart") {
    return renderLinkOrPromptAction(action, "apw-cart-action", true);
  }
  if (action.type === "cart_result") {
    return renderCartResult(action);
  }
  if (["view_cart", "continue_shopping"].includes(action.type)) {
    return renderActionLink(action, `apw-${action.type.replace(/_/g, "-")}-action`);
  }
  if (action.type === "checkout") {
    return renderLinkOrPromptAction(action, "apw-checkout-action", true);
  }
  if (action.type === "redirect") {
    return renderActionLink(action, "apw-redirect-action");
  }
  if (action.type === "sign_in") {
    return renderActionLink(action, "apw-sign-in-action");
  }
  if (action.type === "view_all") {
    return renderActionLink(action, "apw-view-all-action");
  }
  if (action.type === "order_status") {
    return renderOrderStatus(action);
  }
  if (["result_state", "inventory_state", "stale_state"].includes(action.type)) {
    return renderResultState(action);
  }
  if (action.type === "support_handoff" || action.type === "handoff_status") {
    return renderResultState(action);
  }
  if (action.type === "intake_form") {
    return renderInsuranceIntake(action);
  }
  if (action.type === "coverage_card") {
    return renderInsuranceEstimate(action);
  }
  if (action.type === "policy_source" || action.type === "citation_list") {
    return renderCitationList(action);
  }
  if (action.type === "voice_status") {
    return renderVoiceStatusAction(action);
  }
  if (action.type === "try_on") {
    return renderTryOnAction(action);
  }
  if (action.type === "try_on_status") {
    return renderResultState(action);
  }
  if (action.type === "try_on_result") {
    return renderTryOnResult(action);
  }
  if (action.type === "preferences_summary") {
    return renderPreferencesSummary(action);
  }
  if (["preference_update", "preference_forget", "preference_opt_out"].includes(action.type)) {
    return renderPersonalizationMutationAction(action);
  }
  if (["retry", "switch_to_text"].includes(action.type)) {
    return renderPromptAction(action);
  }
  if (action.type === "quick_reply") {
    return renderPromptAction(action);
  }
  if (action.type === "contact_support") {
    return renderContactSupport(action);
  }
  return "";
}

export function renderContentGrid(action: WidgetAction, kind: "article" | "case_study"): string {
  const presentation = actionPresentation(action);
  const title = stringValue(action.data.title) || stringValue(presentation.headline) || "Explore";
  const summary = stringValue(action.data.summary) || stringValue(presentation.description);
  const items = recordArray(action.data.items).slice(0, 6);
  const url = stringValue(action.data.url);
  if (!items.length) {
    return "";
  }
  const sectionLabel = kind === "case_study" ? "OUR WORK" : "INSIGHTS";
  const itemType = kind === "case_study" ? "case_study_card" : "article_card";
  return `<section class="apw-content-grid" data-kind="${kind}">
    <div class="apw-content-grid-head"><div><span>${sectionLabel}</span><strong>${escapeHtml(title)}</strong>${summary ? `<small>${escapeHtml(summary)}</small>` : ""}</div>${renderContentLink(action, url, "View all")}</div>
    <div class="apw-content-grid-items">${items
      .map((item, index) => renderContentCard(actionForItem(action, itemType, `${action.id}-${index + 1}`, item), kind))
      .join("")}</div>
    ${renderTrustBadge(action)}
  </section>`;
}

export function renderContentCard(
  action: WidgetAction,
  kind: "article" | "case_study" | "person" | "service" | "job" | "navigation"
): string {
  const presentation = actionPresentation(action);
  const data = action.data;
  const title = stringValue(data.title) || stringValue(presentation.headline) || stringValue(data.label);
  const summary = stringValue(data.summary) || stringValue(data.description) || stringValue(presentation.description);
  const url = stringValue(data.url) || stringValue(actionExecution(action).url);
  const imageUrl = stringValue(data.imageUrl) || stringValue(data.image_url);
  const eyebrow = contentCardEyebrow(data, kind);
  const cta = stringValue(data.label) || stringValue(presentation.label) || contentCardCta(kind);
  if (!title) {
    return "";
  }
  const personTitleParts = kind === "person" ? title.split(/\s+[—–]\s+/, 2) : [];
  const titleMarkup = personTitleParts.length > 1
    ? `<strong>${escapeHtml(personTitleParts[0] || title)}</strong><span class="apw-person-role">${escapeHtml(personTitleParts[1] || "")}</span>`
    : `<strong>${escapeHtml(title)}</strong>`;
  return `<article class="apw-content-card" data-kind="${kind}">
    ${safeUrl(imageUrl) ? `<div class="apw-content-card-media"><img src="${escapeAttribute(imageUrl)}" alt=""></div>` : renderContentCardGlyph(title, kind)}
    <div class="apw-content-card-body">
      ${eyebrow ? `<span class="apw-content-card-eyebrow">${escapeHtml(eyebrow)}</span>` : ""}
      ${titleMarkup}
      ${summary ? `<p>${escapeHtml(summary)}</p>` : ""}
      ${kind === "case_study" ? renderCaseStudyOutcomes(data) : ""}
      ${renderContentLink(action, url, cta)}
    </div>
  </article>`;
}

export function renderContentCardGlyph(
  title: string,
  kind: "article" | "case_study" | "person" | "service" | "job" | "navigation"
): string {
  if (kind === "article") {
    return "";
  }
  if (kind === "case_study") {
    return `<span class="apw-content-card-glyph" data-glyph="case_study" aria-hidden="true">${sotmSignalIcon("apw-content-card-signal")}</span>`;
  }
  if (kind === "person") {
    const initials = title
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");
    return `<span class="apw-content-card-glyph" data-glyph="person" aria-hidden="true">${escapeHtml(initials || "S")}</span>`;
  }
  const icons: Record<"service" | "job" | "navigation", string> = {
    service: `<svg viewBox="0 0 24 24" focusable="false"><path d="M5 7.5h14M5 12h9M5 16.5h11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square"></path></svg>`,
    job: `<svg viewBox="0 0 24 24" focusable="false"><path d="M5.5 8.5h13a1.5 1.5 0 0 1 1.5 1.5v8H4v-8a1.5 1.5 0 0 1 1.5-1.5Zm3 0V6h7v2.5M4 12.5h16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"></path></svg>`,
    navigation: sotmSignalIcon("apw-content-card-signal")
  };
  return `<span class="apw-content-card-glyph" data-glyph="${kind}" aria-hidden="true">${icons[kind]}</span>`;
}

export function contentCardEyebrow(data: Record<string, unknown>, kind: string): string {
  const publishedAt = stringValue(data.publishedAt) || stringValue(data.published_at);
  if (kind === "job") {
    return [
      stringValue(data.workArrangement) || stringValue(data.work_arrangement),
      stringValue(data.location),
      stringValue(data.department) || stringValue(data.category)
    ].filter(Boolean).join(" · ");
  }
  if (kind === "case_study") {
    return [
      stringValue(data.industry),
      stringValue(data.clientLabel) || stringValue(data.client_label)
    ].filter(Boolean).join(" · ");
  }
  const parts = [
    stringValue(data.category) || stringValue(data.department),
    formatContentDate(publishedAt) || stringValue(data.location),
    stringValue(data.workArrangement) || stringValue(data.work_arrangement) || stringValue(data.role)
  ].filter(Boolean);
  return parts.join(" · ") || (kind === "navigation" ? "EXPLORE SOTM" : "");
}

export function formatContentDate(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return value;
  }
  const [, year, month, day] = match;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthLabel = monthNames[Math.max(0, Math.min(11, Number(month) - 1))];
  return `${monthLabel} ${Number(day)}, ${year}`;
}

export function contentCardCta(kind: string): string {
  const labels: Record<string, string> = {
    article: "Read article",
    case_study: "View case study",
    person: "Meet the team",
    service: "Explore service",
    job: "View position",
    navigation: "Open page"
  };
  return labels[kind] || "Open";
}

export function renderCaseStudyOutcomes(data: Record<string, unknown>): string {
  const outcomes = recordArray(data.outcomes).slice(0, 3);
  if (!outcomes.length) return "";
  return `<div class="apw-case-study-outcomes">${outcomes.map((outcome) => {
    const value = stringValue(outcome.value);
    const label = stringValue(outcome.label);
    return `<span>${value ? `<b>${escapeHtml(value)}</b>` : ""}${label ? `<small>${escapeHtml(label)}</small>` : ""}</span>`;
  }).join("")}</div>`;
}

export function renderContentLink(action: WidgetAction, url: string, label: string): string {
  if (!safeUrl(url)) {
    return "";
  }
  const execution = actionExecution(action);
  const configuredTarget = stringValue(execution.target) || stringValue(action.data.target);
  const sameTab = ["same_tab", "_self", "self"].includes(configuredTarget);
  return `<a class="apw-content-link apw-action-open" href="${escapeAttribute(url)}" target="${sameTab ? "_self" : "_blank"}" ${sameTab ? "" : 'rel="noreferrer"'} ${actionDataAttributes(action, url)}><span>${escapeHtml(label)}</span><b aria-hidden="true">&#8599;</b></a>`;
}

export function renderProductGrid(action: WidgetAction): string {
  const presentation = actionPresentation(action);
  const title = stringValue(action.data.title) || stringValue(presentation.headline) || "Shop these options";
  const summary = stringValue(action.data.summary) || stringValue(presentation.description);
  const allItems = recordArray(action.data.items);
  const items = allItems.slice(0, 10);
  const resultCount = positiveInteger(action.data.resultCount) ?? allItems.length;
  const searchUrl = stringValue(action.data.searchUrl);
  const refinements = recordArray(action.data.refinements).slice(0, 4);
  if (!items.length) {
    return "";
  }
  return `
    <div class="apw-product-grid" ${actionDataAttributes(action, safeUrl(searchUrl) ? searchUrl : "")}>
      <div class="apw-product-grid-head">
        <div>
          <strong>${escapeHtml(title)}</strong>
          ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
          <em class="apw-product-count">${escapeHtml(`${items.length} visible`)}</em>
        </div>
        ${safeUrl(searchUrl) ? `<a class="apw-inline-cta apw-action-open" href="${escapeAttribute(searchUrl)}" target="_blank" rel="noreferrer" ${actionDataAttributes(action, searchUrl)}><span class="apw-view-all-default">View all</span><span class="apw-view-all-fashion">View All Results (${escapeHtml(String(resultCount || items.length))})</span></a>` : ""}
      </div>
      <div class="apw-product-grid-items">
        ${items.map((item, index) => renderGridProduct(action, item, index)).join("")}
      </div>
      ${
        refinements.length
          ? `<div class="apw-refinement-bar">${refinements
              .map((refinement, index) => renderRefinement(action, refinement, index))
              .join("")}</div>`
          : ""
      }
      ${renderProductPriceRange(action, items)}
      ${renderTrustBadge(action)}
    </div>
  `;
}

export function renderGridProduct(action: WidgetAction, item: Record<string, unknown>, index: number): string {
  const title = stringValue(item.title) || "Product";
  const sku = stringValue(item.sku) || `item-${index + 1}`;
  const imageUrl = stringValue(item.imageUrl);
  const url = stringValue(item.url);
  const cartUrl = stringValue(item.cartUrl);
  const price = numberValue(item.price);
  const priceHtml = renderPriceStack(item);
  const badge = saleBadge(item);
  const availability = stringValue(item.availabilityLabel);
  const optionSummary = stringValue(item.optionSummary);
  const swatches = recordArray(item.swatches).slice(0, 4);
  const tryOnEnabled = booleanValue(item.tryOnEnabled ?? item.try_on_enabled, true);
  const cartEnabled = booleanValue(item.cartEnabled ?? item.cart_enabled, true);
  const tryOnPrompt = stringValue(item.tryOnPrompt) || `Help me virtually try on or style ${title}.`;
  const cartAction = actionForItem(action, "add_to_cart", `cart-${sku}`, {
    ...item,
    url: cartUrl,
    label: "Add to cart",
    destinationKind: "shopify_cart",
    requiresConfirmation: true,
    confirmation: stringValue(item.cartConfirmation) || `Open cart for ${title}?`
  });
  const viewAction = actionForItem(action, "product_card", `view-${sku}`, {
    ...item,
    url,
    label: "View",
    destinationKind: "product_detail"
  });
  const tryOnAction = actionForItem(action, "try_on", `try-on-${sku}`, {
    ...item,
    prompt: tryOnPrompt,
    label: "Try on",
    destinationKind: "style_preview"
  });
  return `
    <article class="apw-product-card apw-product-card-rich" data-price="${escapeAttribute(price === null ? "" : String(price))}" data-title="${escapeAttribute(title)}">
      <div class="apw-product-media">
        ${imageUrl && safeUrl(imageUrl) ? `<img src="${escapeAttribute(imageUrl)}" alt="">` : `<span class="apw-product-card-thumb" aria-hidden="true"></span>`}
        <span class="apw-product-icon" aria-hidden="true"></span>
        ${badge ? `<span class="apw-sale-badge">${escapeHtml(badge)}</span>` : ""}
        <div class="apw-fashion-product-controls">
          ${tryOnEnabled ? `<button class="apw-try-on" type="button" data-prompt="${escapeAttribute(tryOnPrompt)}" data-title="${escapeAttribute(title)}" data-image-url="${escapeAttribute(imageUrl)}" data-product-url="${escapeAttribute(url)}" ${actionDataAttributes(tryOnAction, "")}>Try on</button>` : ""}
          ${cartEnabled && safeActionUrl(cartAction, cartUrl) ? `<a class="apw-icon-cta apw-action-open" href="${escapeAttribute(cartUrl)}" target="_blank" rel="noreferrer" data-confirm="${escapeAttribute(stringValue(cartAction.data.confirmation) || `Open cart for ${title}?`)}" ${actionDataAttributes(cartAction, cartUrl)} aria-label="Add ${escapeAttribute(title)} to cart">Cart</a>` : ""}
        </div>
      </div>
      <div class="apw-product-card-body">
        ${safeUrl(url) ? `<a class="apw-product-title-link apw-action-open" href="${escapeAttribute(url)}" target="_blank" rel="noreferrer" ${actionDataAttributes(viewAction, url)}>${escapeHtml(title)}</a>` : ""}
        <strong>${escapeHtml(title)}</strong>
        ${optionSummary ? `<small>${escapeHtml(optionSummary)}</small>` : ""}
        ${priceHtml}
        ${availability ? `<span class="apw-action-pill">${escapeHtml(availability)}</span>` : ""}
        ${
          swatches.length
            ? `<div class="apw-swatches">${swatches
                .map((swatch) => `<span title="${escapeAttribute(stringValue(swatch.label) || stringValue(swatch.value))}">${escapeHtml((stringValue(swatch.label) || stringValue(swatch.value)).slice(0, 1).toUpperCase())}</span>`)
                .join("")}</div>`
            : ""
        }
      </div>
      <div class="apw-product-controls">
        ${tryOnEnabled ? `<button class="apw-try-on" type="button" data-prompt="${escapeAttribute(tryOnPrompt)}" data-title="${escapeAttribute(title)}" data-image-url="${escapeAttribute(imageUrl)}" data-product-url="${escapeAttribute(url)}" ${actionDataAttributes(tryOnAction, "")}>Try on</button>` : ""}
        ${cartEnabled && safeActionUrl(cartAction, cartUrl) ? `<a class="apw-icon-cta apw-action-open" href="${escapeAttribute(cartUrl)}" target="_blank" rel="noreferrer" data-confirm="${escapeAttribute(stringValue(cartAction.data.confirmation) || `Open cart for ${title}?`)}" ${actionDataAttributes(cartAction, cartUrl)} aria-label="Add ${escapeAttribute(title)} to cart">Cart</a>` : ""}
        ${safeUrl(url) ? `<a class="apw-inline-cta apw-action-open" href="${escapeAttribute(url)}" target="_blank" rel="noreferrer" ${actionDataAttributes(viewAction, url)}>View</a>` : ""}
      </div>
    </article>
  `;
}

export function renderRefinement(action: WidgetAction, refinement: Record<string, unknown>, index: number): string {
  const label = stringValue(refinement.label) || "Refine";
  const prompt = stringValue(refinement.prompt) || label;
  const refinementAction = actionForItem(action, "quick_reply", stringValue(refinement.id) || `refine-${index + 1}`, {
    label,
    prompt,
    category: "commerce_refinement"
  });
  return `<button class="apw-quick-reply apw-grid-refinement" type="button" data-prompt="${escapeAttribute(prompt)}" ${actionDataAttributes(refinementAction, "")}>${escapeHtml(label)}</button>`;
}

export function renderProductCard(action: WidgetAction): string {
  const presentation = actionPresentation(action);
  const execution = actionExecution(action);
  const title = stringValue(action.data.title) || stringValue(presentation.headline) || "Product";
  const sku = stringValue(action.data.sku);
  const imageUrl = stringValue(action.data.imageUrl);
  const url = stringValue(execution.url) || stringValue(action.data.url);
  const priceHtml = renderPriceStack(action.data);
  const quantity = stringValue(action.data.availabilityLabel) || (typeof action.data.totalQuantity === "number" ? `${action.data.totalQuantity} in stock` : "");
  const tags = stringArray(action.data.tags).slice(0, 3);
  const badge = saleBadge(action.data);
  const ctaLabel = stringValue(presentation.label) || "View product";
  const body = `
    <div class="apw-product-media">
      ${imageUrl && safeUrl(imageUrl) ? `<img src="${escapeAttribute(imageUrl)}" alt="">` : `<span class="apw-product-card-thumb" aria-hidden="true"></span>`}
      ${badge ? `<span class="apw-sale-badge">${escapeHtml(badge)}</span>` : ""}
    </div>
    <div class="apw-product-card-body">
      <strong>${escapeHtml(title)}</strong>
      <small>${escapeHtml(sku)}</small>
      ${priceHtml}
      ${quantity ? `<span class="apw-action-pill">${escapeHtml(quantity)}</span>` : ""}
      ${tags.length ? `<div class="apw-action-tags">${tags.map((tag) => `<em>${escapeHtml(tag)}</em>`).join("")}</div>` : ""}
      ${renderTrustBadge(action)}
    </div>
  `;
  if (safeUrl(url)) {
    return `
      <div class="apw-product-card" ${actionDataAttributes(action, url)}>
        ${body}
        <a class="apw-inline-cta apw-action-open" href="${escapeAttribute(url)}" target="_blank" rel="noreferrer" ${actionDataAttributes(action, url)}>
          ${escapeHtml(ctaLabel)}
        </a>
      </div>
    `;
  }
  return `<div class="apw-product-card" ${actionDataAttributes(action, "")}>${body}</div>`;
}

export function renderTryOnAction(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Style preview";
  const imageUrl = stringValue(action.data.imageUrl);
  const productUrl = stringValue(action.data.url);
  const prompt = stringValue(action.data.prompt) || `Help me style ${title}.`;
  return `
    <div class="apw-try-on-card" ${actionDataAttributes(action, "")}>
      ${imageUrl && safeUrl(imageUrl) ? `<img src="${escapeAttribute(imageUrl)}" alt="">` : `<span class="apw-product-card-thumb" aria-hidden="true"></span>`}
      <div>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(stringValue(action.data.optionSummary) || "Fit and styling preview")}</small>
        <button class="apw-try-on" type="button" data-prompt="${escapeAttribute(prompt)}" data-title="${escapeAttribute(title)}" data-image-url="${escapeAttribute(imageUrl)}" data-product-url="${escapeAttribute(productUrl)}" ${actionDataAttributes(action, "")}>Try on</button>
      </div>
      ${renderTrustBadge(action)}
    </div>
  `;
}

export function renderComparison(action: WidgetAction): string {
  const view = comparisonPublicView(action.data);
  if (!view.items.length) {
    return "";
  }
  return `
    <section class="apw-comparison" aria-label="${escapeAttribute(view.title)}" ${actionDataAttributes(action, "")}>
      <strong>${escapeHtml(view.title)}</strong>
      ${view.summary ? `<p>${escapeHtml(view.summary)}</p>` : ""}
      <span class="apw-comparison-evidence-status" data-status="${escapeAttribute(view.evidenceStatus.status)}" role="status">${escapeHtml(view.evidenceStatus.label)}</span>
      <p class="apw-comparison-no-winner">No overall winner is asserted. The better option depends on the criterion that matters to you.</p>
      <div class="apw-comparison-items">
        ${view.items.map((item) => renderComparisonItem(action, item)).join("")}
      </div>
      ${renderComparisonCriteria(view.criteria)}
      ${renderComparisonRecommendations(view.recommendations)}
      <div class="apw-comparison-dots" aria-hidden="true">${view.items.map((_, index) => `<i${index === 0 ? ` data-active="true"` : ""}></i>`).join("")}</div>
      ${renderTrustBadge(action)}
    </section>
  `;
}

export function comparisonPublicView(data: Record<string, unknown>): ComparisonPublicView {
  const sourceItems = recordArray(data.items).slice(0, 4);
  const items = sourceItems.map(comparisonPublicItem);
  const requested = comparisonRequestedCriteria(data);
  const criteria = requested.map((criterion) => comparisonPublicCriterion(criterion, data, items));
  return {
    title: comparisonPublicSummary(data.title) || "Compare options",
    summary: comparisonPublicSummary(data.summary),
    items,
    criteria,
    recommendations: comparisonPublicRecommendations(data, criteria, items),
    evidenceStatus: comparisonDerivedEvidenceStatus(requested, items)
  };
}

export function comparisonPublicItem(source: Record<string, unknown>, index: number): ComparisonPublicItem {
  const rawTitle = stringValue(source.title) || stringValue(source.productTitle);
  const title = comparisonPublicTitle(rawTitle) || `Option ${index + 1}`;
  const identityVerified = comparisonItemIdentityVerified(source);
  const priceLabel = comparisonVerifiedPriceLabel(source);
  const availabilityLabel = comparisonVerifiedAvailabilityLabel(source);
  return {
    source,
    matchKeys: identityVerified ? comparisonItemMatchKeys(source) : new Set<string>(),
    identityVerified,
    title,
    imageUrl: safeUrl(stringValue(source.imageUrl)) ? stringValue(source.imageUrl) : "",
    url: safeUrl(stringValue(source.url)) ? stringValue(source.url) : "",
    badge: comparisonPublicBadge(source.badge) || comparisonPublicBadge(source.label),
    priceLabel,
    availabilityLabel,
    evidence: comparisonPublicItemEvidence(source, priceLabel, availabilityLabel)
  };
}

export function renderComparisonItem(action: WidgetAction, item: ComparisonPublicItem): string {
  return `<div class="apw-comparison-item">
    <div class="apw-comparison-media">
      ${item.imageUrl ? `<img src="${escapeAttribute(item.imageUrl)}" alt="">` : `<span class="apw-product-card-thumb" aria-hidden="true"></span>`}
      ${item.badge ? `<span>${escapeHtml(item.badge)}</span>` : ""}
    </div>
    <span>${escapeHtml(item.title)}</span>
    <small>${escapeHtml([item.priceLabel, item.availabilityLabel].filter(Boolean).join(" · "))}</small>
    ${item.evidence.length ? `<dl class="apw-comparison-item-evidence">${item.evidence.map((entry) => `<div data-status="${escapeAttribute(entry.status)}"><dt>${escapeHtml(entry.label)}</dt><dd>${escapeHtml(entry.value)}</dd></div>`).join("")}</dl>` : ""}
    ${item.url ? `<a class="apw-inline-cta apw-action-open" href="${escapeAttribute(item.url)}" target="_blank" rel="noreferrer" aria-label="${escapeAttribute(`View ${item.title}`)}" ${actionDataAttributes(action, item.url, item.title)}>View</a>` : ""}
  </div>`;
}

export function comparisonRequestedCriteria(data: Record<string, unknown>): Array<Record<string, unknown>> {
  const declared = recordArray(data.requestedCriteria);
  const embedded = recordArray(data.criteria).filter((criterion) => criterion.requested === true);
  const requested = declared.length ? declared : embedded;
  const byKey = new Map<string, Record<string, unknown>>();
  for (const criterion of requested) {
    const key = comparisonCriterionKey(criterion);
    if (key && comparisonCriterionField(key)) {
      byKey.set(key, criterion);
    }
  }
  return [...byKey.values()].slice(0, 6);
}

export function comparisonPublicCriterion(
  requested: Record<string, unknown>,
  data: Record<string, unknown>,
  items: ComparisonPublicItem[]
): ComparisonPublicCriterion {
  const key = comparisonCriterionKey(requested);
  const label = comparisonCriterionLabel(requested);
  const candidates = recordArray(data.criteria).filter((criterion) => comparisonCriterionKey(criterion) === key);
  const fallback: ComparisonPublicCriterion = {
    key,
    label,
    status: "insufficient",
    selectedItem: null,
    outcome: "More verified evidence is needed for this criterion."
  };
  if (candidates.length !== 1 || !items.length || !comparisonCriterionEvidenceVerified(key, items)) {
    return fallback;
  }
  const candidate = candidates[0] || {};
  const derived = comparisonDerivedCriterion(key, items);
  const status = stringValue(candidate.status).toLowerCase();
  const outcome = stringValue(candidate.outcome).toLowerCase();
  if (derived.status === "tie" && (status === "tie" || outcome === "tie")) {
    return {
      ...fallback,
      status: "tie",
      outcome: "The verified evidence does not identify one option for this criterion."
    };
  }
  if (derived.status === "no_match" && (status === "no_match" || outcome === "no_match")) {
    return {
      ...fallback,
      status: "no_match",
      outcome: "No displayed option has verified evidence matching this criterion."
    };
  }
  if (
    derived.status === "not_comparable" &&
    (status === "not_comparable" || outcome === "not_comparable")
  ) {
    return {
      ...fallback,
      status: "not_comparable",
      outcome: "The available verified evidence cannot be compared safely."
    };
  }
  const supported =
    status === "supported" ||
    (status === "complete" && outcome === "winner") ||
    (status === "complete" && outcome === "unique_lowest_price");
  if (!supported) {
    return fallback;
  }
  const selectedItem = comparisonResolveItem(candidate, items, false);
  if (
    derived.status !== "supported" ||
    !derived.selectedItem ||
    !selectedItem ||
    selectedItem !== derived.selectedItem ||
    !comparisonItemCriterionEvidenceVerified(selectedItem, key)
  ) {
    return fallback;
  }
  return {
    key,
    label,
    status: "supported",
    selectedItem,
    outcome: `${selectedItem.title} is supported for this criterion.`
  };
}

export function comparisonPublicRecommendations(
  data: Record<string, unknown>,
  criteria: ComparisonPublicCriterion[],
  items: ComparisonPublicItem[]
): Array<{ criterion: string; title: string; outcome: string }> {
  const recommendations = recordArray(data.recommendations);
  const output: Array<{ criterion: string; title: string; outcome: string }> = [];
  for (const criterion of criteria) {
    if (criterion.status !== "supported" || !criterion.selectedItem) {
      continue;
    }
    const candidates = recommendations.filter(
      (recommendation) =>
        recommendation.conditional === true && comparisonCriterionKey(recommendation) === criterion.key
    );
    if (candidates.length !== 1) {
      continue;
    }
    const selectedItem = comparisonResolveItem(candidates[0] || {}, items, true);
    if (
      !selectedItem ||
      selectedItem !== criterion.selectedItem ||
      !comparisonItemCriterionEvidenceVerified(selectedItem, criterion.key)
    ) {
      continue;
    }
    output.push({
      criterion: criterion.label,
      title: selectedItem.title,
      outcome: `Supported by verified ${criterion.label.toLowerCase()} evidence.`
    });
  }
  return output.slice(0, 4);
}

export function comparisonResolveItem(
  reference: Record<string, unknown>,
  items: ComparisonPublicItem[],
  includeTitle: boolean
): ComparisonPublicItem | null {
  const selected = parseRecord(reference.selected_item ?? reference.selectedItem);
  const values = [
    reference.winner_sku,
    reference.winnerSku,
    reference.winner_title,
    reference.winnerTitle,
    reference.sku,
    reference.id,
    reference.variantId,
    reference.variant_id,
    selected.sku,
    selected.id,
    selected.variantId,
    selected.variant_id,
    selected.title,
    includeTitle ? reference.title : ""
  ]
    .map(comparisonMatchKey)
    .filter(Boolean);
  if (!values.length) {
    return null;
  }
  const matches = items.filter((item) => values.every((value) => item.matchKeys.has(value)));
  return matches.length === 1 ? matches[0] || null : null;
}

export function comparisonItemMatchKeys(item: Record<string, unknown>): Set<string> {
  return new Set(
    [
      item.sku,
      item.id,
      item.variantId,
      item.variant_id,
      item.title,
      item.productTitle,
      item.product_title
    ]
      .map(comparisonMatchKey)
      .filter(Boolean)
  );
}

export function comparisonMatchKey(value: unknown): string {
  return stringValue(value).toLowerCase().replace(/\s+/g, " ");
}

export function comparisonCriterionKey(criterion: Record<string, unknown>): string {
  const field = (stringValue(criterion.criterion) || stringValue(criterion.id) || stringValue(criterion.name)).toLowerCase();
  const target = stringValue(criterion.target).toLowerCase();
  if (field === "lowest_price") {
    return "price:lowest";
  }
  if (field.endsWith("_material")) {
    return `material:${field.replace(/_material$/, "")}`;
  }
  if (field.endsWith("_fit")) {
    return `fit:${field.replace(/_fit$/, "")}`;
  }
  return field ? `${field}:${target}` : "";
}

export function comparisonCriterionField(key: string): string {
  const field = key.split(":", 1)[0] || "";
  return ["price", "material", "fit", "color", "care", "occasion", "availability", "stock"].includes(field)
    ? field
    : "";
}

export function comparisonCriterionLabel(criterion: Record<string, unknown>): string {
  const key = comparisonCriterionKey(criterion);
  const [field = "", target = ""] = key.split(":", 2);
  if (field === "price" && target === "lowest") {
    return "Lowest price";
  }
  if (target) {
    return `${comparisonTitleCase(target)} ${comparisonTitleCase(field)}`;
  }
  return comparisonTitleCase(field || "Comparison");
}

export function comparisonCriterionEvidenceVerified(key: string, items: ComparisonPublicItem[]): boolean {
  return items.length > 1 && items.every((item) => comparisonItemCriterionEvidenceVerified(item, key));
}

export function comparisonDerivedCriterion(
  key: string,
  items: ComparisonPublicItem[]
): ComparisonDerivedCriterion {
  const [field = "", target = ""] = key.split(":", 2);
  if (field === "price" && target === "lowest") {
    const prices = items.map((item) => ({ item, price: comparisonVerifiedPrice(item.source) }));
    if (prices.some(({ price }) => !price)) {
      return { status: "insufficient", selectedItem: null };
    }
    const currencies = new Set(prices.map(({ price }) => price?.currency || ""));
    if (currencies.size !== 1) {
      return { status: "not_comparable", selectedItem: null };
    }
    const minimum = Math.min(...prices.map(({ price }) => price?.amount ?? Number.POSITIVE_INFINITY));
    const matches = prices.filter(({ price }) => price?.amount === minimum).map(({ item }) => item);
    return matches.length === 1
      ? { status: "supported", selectedItem: matches[0] || null }
      : { status: "tie", selectedItem: null };
  }
  if (!field || !target) {
    return { status: "insufficient", selectedItem: null };
  }
  const matches = items.filter((item) => {
    const evidence =
      field === "availability" || field === "stock"
        ? comparisonStockEvidence(item.source)
        : comparisonEvidence(item.source, field);
    if (comparisonEvidenceState(evidence) !== "verified") {
      return false;
    }
    if (field === "availability" || field === "stock") {
      return comparisonControlledTextMatchesTarget(item.availabilityLabel, target);
    }
    return comparisonEvidenceMatchesTarget(field, evidence.value, target);
  });
  if (matches.length === 1) {
    return { status: "supported", selectedItem: matches[0] || null };
  }
  return matches.length > 1
    ? { status: "tie", selectedItem: null }
    : { status: "no_match", selectedItem: null };
}

export function comparisonEvidenceMatchesTarget(field: string, value: unknown, target: string): boolean {
  const publicValue = comparisonEvidencePublicValue(field, value);
  return comparisonControlledTextMatchesTarget(publicValue, target);
}

export function comparisonControlledTextMatchesTarget(value: string, target: string): boolean {
  const normalizedValue = ` ${value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;
  const normalizedTarget = ` ${target.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;
  return normalizedTarget.trim().length > 0 && normalizedValue.includes(normalizedTarget);
}

export function comparisonItemCriterionEvidenceVerified(item: ComparisonPublicItem, key: string): boolean {
  if (!item.identityVerified) {
    return false;
  }
  const field = comparisonCriterionField(key);
  if (field === "price") {
    return Boolean(item.priceLabel);
  }
  if (field === "availability" || field === "stock") {
    return Boolean(item.availabilityLabel) && comparisonEvidenceState(comparisonStockEvidence(item.source)) === "verified";
  }
  const evidence = comparisonEvidence(item.source, field);
  return comparisonEvidenceState(evidence) === "verified" && Boolean(comparisonEvidencePublicValue(field, evidence.value));
}

export function comparisonPublicItemEvidence(
  item: Record<string, unknown>,
  priceLabel: string,
  availabilityLabel: string
): ComparisonPublicEvidence[] {
  const entries: ComparisonPublicEvidence[] = [];
  for (const field of ["price", "material", "fit", "color", "care", "occasion", "stock"]) {
    const evidence = field === "stock" ? comparisonStockEvidence(item) : comparisonEvidence(item, field);
    if (!Object.keys(evidence).length) {
      continue;
    }
    const status = comparisonEvidenceState(evidence);
    const value =
      status === "stale"
        ? "Needs refresh"
        : status !== "verified"
          ? "Unavailable"
          : field === "price"
            ? priceLabel || "Unavailable"
            : field === "stock"
              ? availabilityLabel || "Unavailable"
              : comparisonEvidencePublicValue(field, evidence.value) || "Unavailable";
    entries.push({
      label: field === "stock" ? "Availability" : comparisonTitleCase(field),
      value,
      status: value === "Unavailable" ? "unavailable" : status
    });
  }
  return entries.slice(0, 6);
}

export function comparisonEvidence(item: Record<string, unknown>, field: string): Record<string, unknown> {
  const evidence = nonEmptyRecord(item.comparisonEvidence) || nonEmptyRecord(item.attributes) || {};
  return parseRecord(evidence[field]);
}

export function comparisonStockEvidence(item: Record<string, unknown>): Record<string, unknown> {
  const stock = comparisonEvidence(item, "stock");
  return Object.keys(stock).length ? stock : comparisonEvidence(item, "availability");
}

export function comparisonEvidenceState(evidence: Record<string, unknown>): ComparisonEvidenceState {
  const status = stringValue(evidence.status).toLowerCase();
  if (status === "verified" && comparisonEvidenceHasProvenance(evidence)) {
    return "verified";
  }
  if (["stale", "refresh_required"].includes(status)) {
    return "stale";
  }
  return "unavailable";
}

export function comparisonEvidenceHasProvenance(evidence: Record<string, unknown>): boolean {
  const sourceRef = stringValue(evidence.source_ref) || stringValue(evidence.sourceRef);
  const sourceIds = Array.isArray(evidence.source_ids)
    ? evidence.source_ids
    : Array.isArray(evidence.sourceIds)
      ? evidence.sourceIds
      : [];
  return Boolean(sourceRef || sourceIds.some((sourceId) => Boolean(stringValue(sourceId))));
}

export function comparisonItemIdentityVerified(item: Record<string, unknown>): boolean {
  const identity = nonEmptyRecord(item.identityEvidence) || nonEmptyRecord(item.identity) || {};
  const identityFields: Array<[string, unknown]> = [
    ["sku", item.sku],
    ["product_title", item.title ?? item.productTitle ?? item.product_title],
    ["variant_id", item.variantId ?? item.variant_id],
    ["variant_title", item.variantTitle ?? item.variant_title],
    ["size", item.size]
  ];
  for (const [field, expected] of identityFields) {
    const evidence = parseRecord(identity[field]);
    if (
      stringValue(expected) &&
      comparisonEvidenceState(evidence) === "verified" &&
      comparisonMatchKey(evidence.value) === comparisonMatchKey(expected)
    ) {
      return true;
    }
  }
  const variant = comparisonEvidence(item, "variant");
  if (comparisonEvidenceState(variant) !== "verified") {
    return false;
  }
  const value = parseRecord(variant.value);
  const comparisons: Array<[unknown, unknown]> = [];
  for (const candidate of [
    [value.variant_id ?? value.variantId, item.variantId ?? item.variant_id] as [unknown, unknown],
    [value.size, item.size] as [unknown, unknown]
  ]) {
    if (stringValue(candidate[0])) {
      comparisons.push(candidate);
    }
  }
  return (
    comparisons.length > 0 &&
    comparisons.every(
      ([evidenceValue, expected]) =>
        Boolean(stringValue(expected)) && comparisonMatchKey(evidenceValue) === comparisonMatchKey(expected)
    )
  );
}

export function comparisonEvidencePublicValue(field: string, value: unknown): string {
  if (typeof value === "string") {
    return comparisonPublicText(value);
  }
  if (typeof value === "number" && field !== "price") {
    return comparisonPublicText(String(value));
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => comparisonPublicText(entry))
      .filter(Boolean)
      .slice(0, 3)
      .join(", ");
  }
  return "";
}

export function comparisonVerifiedPriceLabel(item: Record<string, unknown>): string {
  const price = comparisonVerifiedPrice(item);
  return price ? formatPrice(price.amount, price.currency) : "";
}

export function comparisonVerifiedPrice(
  item: Record<string, unknown>
): { amount: number; currency: string } | null {
  const evidence = comparisonEvidence(item, "price");
  if (comparisonEvidenceState(evidence) !== "verified") {
    return null;
  }
  const evidenceValue = evidence.value;
  const structured = parseRecord(evidenceValue);
  const evidenceAmount = numberValue(structured.amount) ?? numberValue(evidenceValue);
  let evidenceCurrency = stringValue(structured.currency).toUpperCase();
  if (!evidenceCurrency) {
    const currencyEvidence = comparisonEvidence(item, "currency");
    if (comparisonEvidenceState(currencyEvidence) !== "verified") {
      return null;
    }
    evidenceCurrency = stringValue(currencyEvidence.value).toUpperCase();
  }
  const itemAmount = numberValue(item.price);
  const itemCurrency = stringValue(item.currency).toUpperCase();
  if (
    evidenceAmount === null ||
    itemAmount === null ||
    evidenceAmount !== itemAmount ||
    !evidenceCurrency ||
    !itemCurrency ||
    evidenceCurrency !== itemCurrency
  ) {
    return null;
  }
  return { amount: itemAmount, currency: itemCurrency };
}

export function comparisonVerifiedAvailabilityLabel(item: Record<string, unknown>): string {
  const evidence = comparisonStockEvidence(item);
  if (comparisonEvidenceState(evidence) !== "verified") {
    return "";
  }
  const value = evidence.value;
  const structured = parseRecord(value);
  const explicitCount = numberValue(item.totalQuantity);
  const evidenceCount = numberValue(structured.total_quantity ?? structured.totalQuantity);
  const available =
    typeof structured.available === "boolean"
      ? structured.available
      : comparisonAvailabilityBoolean(value);
  if (evidenceCount !== null && available !== null && available !== (evidenceCount > 0)) {
    return "";
  }
  if (explicitCount !== null) {
    if (evidenceCount === null || explicitCount !== evidenceCount) {
      return "";
    }
    return `${explicitCount} in stock`;
  }
  if (available !== null) {
    return available ? "Available" : "Out of stock";
  }
  if (evidenceCount !== null) {
    return evidenceCount > 0 ? "Available" : "Out of stock";
  }
  return "";
}

export function comparisonAvailabilityBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }
  const normalized = stringValue(value).toLowerCase();
  if (["available", "in_stock", "in stock", "true"].includes(normalized)) {
    return true;
  }
  if (["unavailable", "out_of_stock", "out of stock", "false"].includes(normalized)) {
    return false;
  }
  return null;
}

export function comparisonDerivedEvidenceStatus(
  requested: Array<Record<string, unknown>>,
  items: ComparisonPublicItem[]
): { status: string; label: string } {
  const states = requested.flatMap((criterion) => {
    const key = comparisonCriterionKey(criterion);
    const field = comparisonCriterionField(key);
    return items.map((item) => {
      if (comparisonItemCriterionEvidenceVerified(item, key)) {
        return "verified";
      }
      const evidence = field === "availability" || field === "stock"
        ? comparisonStockEvidence(item.source)
        : comparisonEvidence(item.source, field);
      return comparisonEvidenceState(evidence) === "stale" ? "stale" : "unavailable";
    });
  });
  if (!states.length || states.every((status) => status === "unavailable")) {
    return { status: "insufficient", label: "Not enough verified evidence for the requested comparison" };
  }
  if (states.every((status) => status === "verified")) {
    return { status: "verified", label: "Verified comparison evidence" };
  }
  if (states.some((status) => status === "stale") && !states.some((status) => status === "verified")) {
    return { status: "stale", label: "Comparison evidence needs a refresh" };
  }
  return { status: "partial", label: "Some comparison evidence is unavailable" };
}

export function renderComparisonCriteria(criteria: ComparisonPublicCriterion[]): string {
  if (!criteria.length) {
    return "";
  }
  return `<section class="apw-comparison-criteria" aria-label="Comparison outcomes">
    <strong>By criterion</strong>
    <ul>${criteria.map((criterion) => `<li data-status="${escapeAttribute(criterion.status)}"><div><strong>${escapeHtml(criterion.label)}</strong><span>${escapeHtml(comparisonCriterionStatusLabel(criterion.status))}</span></div><p>${escapeHtml(criterion.outcome)}</p></li>`).join("")}</ul>
  </section>`;
}

export function renderComparisonRecommendations(
  recommendations: Array<{ criterion: string; title: string; outcome: string }>
): string {
  if (!recommendations.length) {
    return "";
  }
  return `<section class="apw-comparison-recommendations" aria-label="Conditional recommendations">
    <strong>Conditional recommendations</strong>
    <ul>${recommendations.map((recommendation) => `<li><span>For ${escapeHtml(recommendation.criterion)}</span><strong>${escapeHtml(recommendation.title)}</strong><p>${escapeHtml(recommendation.outcome)}</p></li>`).join("")}</ul>
  </section>`;
}

export function comparisonCriterionStatusLabel(status: ComparisonPublicCriterion["status"]): string {
  const labels: Record<ComparisonPublicCriterion["status"], string> = {
    supported: "Supported",
    tie: "Tie",
    no_match: "No verified match",
    not_comparable: "Not comparable",
    insufficient: "Needs evidence"
  };
  return labels[status];
}

export function comparisonPublicSummary(value: unknown): string {
  const text = comparisonPublicText(value);
  return comparisonContainsGlobalRecommendation(text) ? "" : text;
}

export function comparisonPublicBadge(value: unknown): string {
  const text = comparisonPublicText(value);
  return comparisonContainsGlobalRecommendation(text) ? "" : text;
}

export function comparisonContainsGlobalRecommendation(value: string): boolean {
  return /\b(?:best|winner|top pick|top choice|recommend(?:ed|ation)|preferred|overall|better(?: option| choice| value| match| than)?|my pick|go with|clearest advantage)\b/i.test(value);
}

export function comparisonPublicTitle(value: unknown): string {
  const text = comparisonPublicText(value);
  if (
    !text ||
    /^(?:sku|id|item|product|variant|internal)[-_: #]/i.test(text) ||
    /^\d{4,}$/.test(text) ||
    /^[A-Z0-9][A-Z0-9_-]{5,}$/.test(text)
  ) {
    return "";
  }
  return text;
}

export function comparisonPublicText(value: unknown): string {
  const text = stringValue(value);
  if (!text) {
    return "";
  }
  if (
    /[<>]/.test(text) ||
    /\bgid:\/\//i.test(text) ||
    /\b(?:source|invocation|manifest|lineage|digest)[_ -]?(?:ref|id|sha256)?\b/i.test(text) ||
    /\b(?:sku|variant|product|item|record|internal)[_ -]?(?:id|code)?\s*[:=#]\s*[a-z0-9_-]{3,}\b/i.test(text) ||
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i.test(text) ||
    /\b[0-9a-f]{48,64}\b/i.test(text) ||
    /\b\d{9,}\b/.test(text) ||
    /^[A-Z0-9][A-Z0-9_-]{7,}$/.test(text)
  ) {
    return "";
  }
  return text;
}

export function comparisonTitleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function renderPriceRangeAction(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Price range";
  const currency = stringValue(action.data.currency) || "USD";
  const minimum =
    numberValue(action.data.minimum) ??
    numberValue(action.data.min) ??
    numberValue(action.data.minimumPrice) ??
    numberValue(action.data.minimum_price);
  const maximum =
    numberValue(action.data.maximum) ??
    numberValue(action.data.max) ??
    numberValue(action.data.maximumPrice) ??
    numberValue(action.data.maximum_price);
  const prompt = stringValue(actionExecution(action).prompt) || stringValue(action.data.prompt);
  const rangeLabel = [
    minimum === null ? "" : formatPrice(minimum, currency),
    maximum === null ? "" : formatPrice(maximum, currency)
  ].filter(Boolean).join(" - ");
  return `
    <section class="apw-price-range apw-action-price-range" role="group" aria-label="${escapeAttribute(title)}" ${actionDataAttributes(action, "")}>
      <div>
        <span>${escapeHtml(title)}</span>
        ${rangeLabel ? `<b>${escapeHtml(rangeLabel)}</b>` : `<b>Choose a price range</b>`}
      </div>
      ${prompt ? `<button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(prompt)}" data-display-message="${escapeAttribute(stringValue(action.data.label) || title)}" ${actionDataAttributes(action, "")}>${escapeHtml(stringValue(action.data.label) || "Apply price range")}</button>` : ""}
    </section>
  `;
}

export function renderRecommendation(action: WidgetAction): string {
  const presentation = actionPresentation(action);
  const title = stringValue(action.data.title) || stringValue(presentation.headline) || "Recommendation";
  const summary =
    stringValue(action.data.summary) ||
    stringValue(action.data.rationale) ||
    stringValue(action.data.why) ||
    stringValue(presentation.description);
  const product = parseRecord(action.data.product);
  const fallbackItem = recordArray(action.data.items)[0] || {};
  const recommendedProduct = Object.keys(product).length ? product : fallbackItem;
  return `
    <section class="apw-result-state apw-recommendation" aria-label="${escapeAttribute(title)}" ${actionDataAttributes(action, "")}>
      <div class="apw-result-head"><strong>${escapeHtml(title)}</strong></div>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      ${Object.keys(recommendedProduct).length ? renderProductCard(actionForItem(action, "product_card", `${action.id}-product`, recommendedProduct)) : ""}
      ${renderTrustBadge(action)}
    </section>
  `;
}

export function renderCartResult(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Cart updated";
  const summary = stringValue(action.data.summary) || stringValue(action.data.message);
  const status = stringValue(action.data.status) || "complete";
  const lineCount =
    positiveInteger(action.data.lineCount) ??
    positiveInteger(action.data.line_count) ??
    positiveInteger(action.data.itemCount) ??
    positiveInteger(action.data.item_count);
  const total = numberValue(action.data.total) ?? numberValue(action.data.cartTotal) ?? numberValue(action.data.cart_total);
  const currency = stringValue(action.data.currency) || "USD";
  const rows = [
    ["Status", statusLabel(status)],
    ["Items", lineCount === null ? "" : String(lineCount)],
    ["Total", total === null ? "" : formatPrice(total, currency)]
  ].filter((row): row is [string, string] => Boolean(row[1]));
  return `
    <section class="apw-result-state apw-cart-result" role="status" aria-live="polite" aria-label="${escapeAttribute(title)}" ${actionDataAttributes(action, "")}>
      <div class="apw-result-head"><strong>${escapeHtml(title)}</strong></div>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      ${rows.length ? `<div class="apw-result-meta">${rows.map(([label, value]) => `<span><b>${escapeHtml(label)}</b>${escapeHtml(value)}</span>`).join("")}</div>` : ""}
      ${renderTrustBadge(action)}
    </section>
  `;
}

export function renderTryOnResult(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Try-on preview";
  const summary = stringValue(action.data.summary) || stringValue(action.data.message);
  const limitations =
    stringValue(action.data.limitations) ||
    "Visualization only; appearance, fit, fabric behavior, and availability are not guaranteed.";
  const imageCandidates = [
    stringValue(action.data.imageUrl),
    stringValue(action.data.image_url),
    stringValue(action.data.outputUrl),
    stringValue(action.data.output_url),
    ...stringArray(action.data.images),
    ...stringArray(action.data.generatedImages),
    ...stringArray(action.data.generated_images)
  ];
  const images = [...new Set(imageCandidates.filter((url) => safeUrl(url)))].slice(0, 4);
  const productUrl = stringValue(action.data.productUrl) || stringValue(action.data.product_url) || stringValue(action.data.url);
  return `
    <section class="apw-result-state apw-try-on-result" role="status" aria-live="polite" aria-label="${escapeAttribute(title)}" ${actionDataAttributes(action, "")}>
      <div>
        <strong>${escapeHtml(title)}</strong>
        ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
        ${images.length ? `<div class="apw-try-on-results">${images.map((url) => `<img src="${escapeAttribute(url)}" alt="Generated try-on preview">`).join("")}</div>` : ""}
        <small>${escapeHtml(limitations)}</small>
        ${safeUrl(productUrl) ? renderContentLink(action, productUrl, "View source product") : ""}
      </div>
      ${renderTrustBadge(action)}
    </section>
  `;
}

export function renderPreferencesSummary(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Saved preferences";
  const summary = stringValue(action.data.summary) || stringValue(action.data.message);
  const rawPreferences = Array.isArray(action.data.preferences) ? action.data.preferences.slice(0, 8) : [];
  const preferences = rawPreferences
    .map((item) => {
      if (typeof item === "string") {
        return { label: item, value: "" };
      }
      const record = parseRecord(item);
      return {
        label: stringValue(record.label) || stringValue(record.name) || stringValue(record.key),
        value: stringValue(record.value) || stringValue(record.summary)
      };
    })
    .filter((item) => item.label);
  const optedIn = action.data.optedIn ?? action.data.opted_in;
  return `
    <section class="apw-result-state apw-preferences-summary" aria-label="${escapeAttribute(title)}" ${actionDataAttributes(action, "")}>
      <div class="apw-result-head"><strong>${escapeHtml(title)}</strong></div>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      ${typeof optedIn === "boolean" ? `<span class="apw-action-pill">${optedIn ? "Personalization on" : "Personalization off"}</span>` : ""}
      ${preferences.length ? `<dl class="apw-preference-list">${preferences.map((item) => `<div><dt>${escapeHtml(item.label)}</dt>${item.value ? `<dd>${escapeHtml(item.value)}</dd>` : ""}</div>`).join("")}</dl>` : ""}
    </section>
  `;
}

export function renderWeatherCard(action: WidgetAction): string {
  const title = stringValue(action.data.title) || stringValue(action.data.location) || "Weather context";
  const summary = stringValue(action.data.summary) || stringValue(action.data.description);
  const temperature = stringValue(action.data.temperature) || stringValue(action.data.temperatureLabel);
  const conditions = stringValue(action.data.conditions) || stringValue(action.data.condition);
  const details = [
    stringValue(action.data.high) ? `High ${stringValue(action.data.high)}` : "",
    stringValue(action.data.low) ? `Low ${stringValue(action.data.low)}` : "",
    stringValue(action.data.precipitation) || stringValue(action.data.precipitationChance)
  ].filter(Boolean);
  return `
    <div class="apw-weather-card" ${actionDataAttributes(action, "")}>
      <div>
        <span>Weather</span>
        <strong>${escapeHtml(title)}</strong>
        ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      </div>
      <b>${escapeHtml([temperature, conditions].filter(Boolean).join(" / ") || "Context ready")}</b>
      ${details.length ? `<p>${escapeHtml(details.join(" · "))}</p>` : ""}
    </div>
  `;
}

export function renderContactModule(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Contact preferences";
  const summary = stringValue(action.data.summary) || stringValue(action.data.description) || "Share how the store team should follow up.";
  const fields = stringArray(action.data.fields).length
    ? stringArray(action.data.fields).slice(0, 3)
    : ["Name", "Email", "Request"];
  const cta = stringValue(action.data.label) || stringValue(action.data.ctaLabel) || "Save preferences";
  return `
    <div class="apw-contact-module">
      <strong>${escapeHtml(title)}</strong>
      <small>${escapeHtml(summary)}</small>
      <div>
        ${fields.map((field) => `<label><span>${escapeHtml(field)}</span><input type="text" autocomplete="off"></label>`).join("")}
      </div>
      <button class="apw-inline-cta" type="button">${escapeHtml(cta)}</button>
    </div>
  `;
}

export function renderActionLink(action: WidgetAction, className: string, forceConfirmation = false): string {
  const presentation = actionPresentation(action);
  const execution = actionExecution(action);
  const label = stringValue(presentation.label) || stringValue(action.data.label) || (action.type === "add_to_cart" ? "Add to cart" : "Open");
  const url = stringValue(execution.url) || stringValue(action.data.url) || stringValue(action.data.fallbackUrl);
  if (!safeActionUrl(action, url)) {
    return "";
  }
  const enabled = action.data.enabled !== false;
  const title = stringValue(presentation.headline) || stringValue(action.data.title);
  const detail = stringValue(presentation.description) || stringValue(action.data.sku) || stringValue(action.data.summary);
  const confirm =
    forceConfirmation || action.data.requiresConfirmation === true || execution.confirmationRequired === true
      ? stringValue(execution.confirmation) || stringValue(action.data.confirmation) || "Open this action?"
      : "";
  const dataAttributes = actionDataAttributes(action, url);
  if (!enabled) {
    return `
      <div class="apw-action-link ${className}" data-disabled="true" ${dataAttributes}>
        <strong>${escapeHtml(label)}</strong>
        ${title || detail ? `<small>${escapeHtml([title, detail].filter(Boolean).join(" · "))}</small>` : ""}
        ${renderTrustBadge(action)}
      </div>
    `;
  }
  return `
    <a class="apw-action-link apw-action-open ${className}" href="${escapeAttribute(url)}" target="_blank" rel="noreferrer" ${dataAttributes} ${
      confirm ? `data-confirm="${escapeAttribute(confirm)}"` : ""
    } data-label="${escapeAttribute(label)}" data-confirm-label="${escapeAttribute(stringValue(execution.confirmLabel) || "Confirm")}" data-cancel-label="${escapeAttribute(stringValue(execution.cancelLabel) || "Cancel")}">
      <strong>${escapeHtml(label)}</strong>
      ${title || detail ? `<small>${escapeHtml([title, detail].filter(Boolean).join(" · "))}</small>` : ""}
      ${renderTrustBadge(action)}
    </a>
  `;
}

export function renderLinkOrPromptAction(action: WidgetAction, className: string, forceConfirmation: boolean): string {
  const execution = actionExecution(action);
  const url = stringValue(execution.url) || stringValue(action.data.url) || stringValue(action.data.fallbackUrl);
  if (safeActionUrl(action, url)) {
    return renderActionLink(action, className, forceConfirmation);
  }
  if (url && storefrontBoundActionTypes.has(action.type)) {
    return "";
  }
  return renderPromptAction(action, forceConfirmation, true);
}

export function renderOrderStatus(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Order help";
  const summary = stringValue(action.data.summary) || stringValue(action.data.message);
  const status = stringValue(action.data.status);
  const primaryAction = parseRecord(action.data.primaryAction);
  const primaryLabel = stringValue(primaryAction.label) || stringValue(action.data.label) || "Open";
  const primaryUrl =
    stringValue(primaryAction.url) ||
    stringValue(action.data.orderLookupUrl) ||
    stringValue(action.data.accountUrl) ||
    stringValue(action.data.url);
  const authLabel = action.data.customerAuthenticated === true ? "Signed in" : "Authentication required";
  const nextActions = stringArray(action.data.nextActions).slice(0, 3);
  const accountLink = safeUrl(primaryUrl)
    ? `<a class="apw-inline-cta apw-action-open" href="${escapeAttribute(primaryUrl)}" target="_blank" rel="noreferrer" ${actionDataAttributes(action, primaryUrl)}>${escapeHtml(primaryLabel)}</a>`
    : "";
  return `
    <div class="apw-order-status" ${actionDataAttributes(action, safeUrl(primaryUrl) ? primaryUrl : "")}>
      <strong>${escapeHtml(title)}</strong>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      <div class="apw-order-status-pills">
        ${status ? `<span>${escapeHtml(statusLabel(status))}</span>` : ""}
        <span>${escapeHtml(authLabel)}</span>
      </div>
      ${nextActions.length ? `<div class="apw-order-next">${nextActions.map((item) => `<em>${escapeHtml(item)}</em>`).join("")}</div>` : ""}
      ${accountLink}
      ${renderTrustBadge(action)}
    </div>
  `;
}

export function renderResultState(action: WidgetAction): string {
  const defaultTitles: Record<string, string> = {
    inventory_state: "Availability",
    stale_state: "Information needs a refresh",
    cart_result: "Cart updated",
    support_handoff: "Handoff queued",
    handoff_status: "Handoff queued",
    try_on_status: "Try-on status",
    contact_support: "Contact support"
  };
  const title = stringValue(action.data.title) || defaultTitles[action.type] || "Result";
  const summary = stringValue(action.data.summary) || stringValue(action.data.message);
  const score = numberValue(action.data.score) ?? numberValue(action.data.overallScore);
  const confidence = stringValue(action.data.confidence);
  const status = stringValue(action.data.status);
  const routing = parseRecord(action.data.routing);
  const intake = parseRecord(action.data.intake);
  const nextSteps = [
    ...stringArray(action.data.nextSteps),
    ...stringArray(action.data.next_steps)
  ].slice(0, 4);
  const metaRows = [
    ["Status", status ? statusLabel(status) : ""],
    ["Confidence", confidence ? statusLabel(confidence) : ""],
    ["Queue", stringValue(routing.queue)],
    ["Review", statusLabel(stringValue(routing.review))],
    ["Request", statusLabel(stringValue(intake.requestType))],
    ["Mode", statusLabel(stringValue(intake.regulatedMode))]
  ].filter((row): row is [string, string] => Boolean(row[1]));
  return `
    <div class="apw-result-state" role="status" aria-live="polite" ${actionDataAttributes(action, "")}>
      <div class="apw-result-head">
        <strong>${escapeHtml(title)}</strong>
        ${score === null ? "" : `<span class="apw-score-badge">${escapeHtml(`${Math.round(score)}`)}</span>`}
      </div>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
      ${
        metaRows.length
          ? `<div class="apw-result-meta">${metaRows
              .slice(0, 6)
              .map(([label, value]) => `<span><b>${escapeHtml(label)}</b>${escapeHtml(value)}</span>`)
              .join("")}</div>`
          : ""
      }
      ${
        nextSteps.length
          ? `<ol class="apw-result-steps">${nextSteps
              .map((step) => `<li>${escapeHtml(step)}</li>`)
              .join("")}</ol>`
          : ""
      }
      ${renderTrustBadge(action)}
    </div>
  `;
}

export function renderInsuranceIntake(action: WidgetAction): string {
  const eyebrow = stringValue(action.data.eyebrow) || "Quote detail";
  const title = stringValue(action.data.title) || "Tell us what you want to protect";
  const options = recordArray(action.data.options).slice(0, 8);
  const layout = stringValue(action.data.layout);
  const progress = parseRecord(action.data.progress);
  const currentStep = numberValue(progress.current) ?? numberValue(action.data.step);
  const totalSteps = numberValue(progress.total) ?? numberValue(action.data.totalSteps);
  const progressPercent = currentStep && totalSteps
    ? Math.max(0, Math.min(100, Math.round((currentStep / totalSteps) * 100)))
    : 0;
  return `
    <section class="apw-insurance-intake" aria-label="${escapeAttribute(eyebrow)}">
      ${currentStep && totalSteps ? `<div class="apw-insurance-progress" role="progressbar" aria-label="Quote progress" aria-valuemin="1" aria-valuemax="${totalSteps}" aria-valuenow="${currentStep}">
        <span><b>${escapeHtml(stringValue(progress.label) || "Quote details")}</b><small>Step ${currentStep} of ${totalSteps}</small></span>
        <i aria-hidden="true"><b style="width:${progressPercent}%"></b></i>
      </div>` : ""}
      <p class="apw-insurance-eyebrow">${escapeHtml(eyebrow)}</p>
      <strong>${escapeHtml(title)}</strong>
      ${layout === "location_input" ? `<form class="apw-insurance-location"><input type="text" aria-label="Property ZIP code or state" autocomplete="postal-code" autocapitalize="characters" enterkeyhint="go" placeholder="Enter ZIP code or state" required><button type="submit">Use location</button></form>` : `<div class="apw-insurance-options">
        ${options.map((option) => {
          const label = stringValue(option.label);
          const prompt = stringValue(option.prompt) || stringValue(option.message) || label;
          const hint = stringValue(option.hint);
          const icon = insuranceOptionIcon(label, stringValue(option.icon));
          return `<button class="apw-quick-reply apw-insurance-option" type="button" data-prompt="${escapeAttribute(prompt)}" data-display-message="${escapeAttribute(label)}" ${actionDataAttributes(action, stringValue(option.value))}>
            <span class="apw-insurance-icon" data-icon="${escapeAttribute(icon)}" aria-hidden="true">${insuranceChoiceIcon(icon)}</span>
            <b>${escapeHtml(label)}</b>
            ${hint ? `<small>${escapeHtml(hint)}</small>` : ""}
          </button>`;
        }).join("")}
      </div>`}
    </section>
  `;
}

export function renderInsuranceEstimate(action: WidgetAction): string {
  const facts = recordArray(action.data.facts).slice(0, 8);
  const actions = recordArray(action.data.actions).slice(0, 3);
  return `<section class="apw-insurance-estimate">
    <div class="apw-insurance-estimate-title"><span class="apw-insurance-icon" data-icon="shield" aria-hidden="true">${insuranceChoiceIcon("shield")}</span><div><p class="apw-insurance-eyebrow">${escapeHtml(stringValue(action.data.eyebrow) || "Illustrative estimate")}</p>
    <h3>${escapeHtml(stringValue(action.data.title) || "Homeowners insurance")}</h3></div></div>
    <div class="apw-estimate-price"><small>${escapeHtml(stringValue(action.data.priceLabel))}</small><strong>${escapeHtml(stringValue(action.data.priceRange))}</strong><span>${escapeHtml(stringValue(action.data.subtitle))}</span></div>
    <div class="apw-estimate-facts">${facts.map(fact => `<div><span class="apw-estimate-fact-icon" aria-hidden="true">${insuranceChoiceIcon(stringValue(fact.icon))}</span><span class="apw-estimate-fact-copy"><small>${escapeHtml(stringValue(fact.label))}</small><b>${escapeHtml(stringValue(fact.value))}</b></span></div>`).join("")}</div>
    <p class="apw-estimate-disclaimer">${escapeHtml(stringValue(action.data.disclaimer))}</p>
    <div class="apw-estimate-actions">${actions.map(item => `<button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(stringValue(item.prompt) || stringValue(item.label))}" data-display-message="${escapeAttribute(stringValue(item.label))}"><span class="apw-estimate-action-icon" aria-hidden="true">${insuranceChoiceIcon(stringValue(item.icon))}</span><strong>${escapeHtml(stringValue(item.label))}</strong></button>`).join("")}</div>
  </section>`;
}

export function insuranceOptionIcon(label: string, configuredIcon: string): string {
  const normalizedLabel = label.trim().toLowerCase();
  if (normalizedLabel === "multi-unit") return "multi_unit";
  if (normalizedLabel === "manufactured home") return "mobile_home";
  return configuredIcon || "help";
}

export function renderCitationList(action: WidgetAction): string {
  const sources = normalizeSources(action.data.sources).slice(0, 4);
  if (!sources.length) {
    return "";
  }
  return `
    <div class="apw-citation-list" ${actionDataAttributes(action, "")}>
      <strong>${escapeHtml(stringValue(action.data.title) || "Sources")}</strong>
      ${sources.map((source) => `<small>${escapeHtml(source.title)}</small>`).join("")}
      ${renderTrustBadge(action)}
    </div>
  `;
}

export function renderVoiceStatusAction(action: WidgetAction): string {
  const title = stringValue(action.data.title) || "Voice";
  const summary = stringValue(action.data.summary) || stringValue(action.data.status);
  return `
    <div class="apw-voice-action">
      <strong>${escapeHtml(title)}</strong>
      ${summary ? `<small>${escapeHtml(summary)}</small>` : ""}
    </div>
  `;
}

export function renderPromptAction(action: WidgetAction, forceConfirmation = false, requireExplicitPrompt = false): string {
  const presentation = actionPresentation(action);
  const execution = actionExecution(action);
  const defaultLabels: Record<string, string> = {
    refinement_chip: "Refine",
    remove_refinement: "Remove filter",
    clear_refinements: "Clear filters",
    preference_update: "Update preference",
    preference_forget: "Forget preference",
    preference_opt_out: "Turn off personalization",
    retry: "Try again",
    switch_to_text: "Switch to text"
  };
  const label =
    stringValue(presentation.label) ||
    stringValue(action.data.label) ||
    defaultLabels[action.type] ||
    "Continue";
  const explicitPrompt = stringValue(execution.prompt) || stringValue(action.data.prompt);
  const prompt = explicitPrompt || (requireExplicitPrompt ? "" : label);
  if (!prompt) {
    return `
      <div class="apw-action-link" data-disabled="true" aria-disabled="true" ${actionDataAttributes(action, "")}>
        <strong>${escapeHtml(label)}</strong>
        <small>This action is unavailable because its authorized request is missing.</small>
      </div>
    `;
  }
  const confirmation =
    forceConfirmation || action.data.requiresConfirmation === true || execution.confirmationRequired === true
      ? stringValue(execution.confirmation) ||
        stringValue(action.data.confirmation) ||
        "Confirm before changing saved preferences."
      : "";
  return `
    <button class="apw-quick-reply" type="button" data-prompt="${escapeAttribute(prompt)}" data-display-message="${escapeAttribute(label)}" ${actionDataAttributes(action, "")} ${
      confirmation
        ? `data-confirm="${escapeAttribute(confirmation)}" data-label="${escapeAttribute(label)}" data-confirm-label="${escapeAttribute(stringValue(execution.confirmLabel) || "Confirm")}" data-cancel-label="${escapeAttribute(stringValue(execution.cancelLabel) || "Cancel")}"`
        : ""
    }>
      ${escapeHtml(label)}
    </button>
  `;
}

export function renderPersonalizationMutationAction(action: WidgetAction): string {
  const status = stringValue(action.data.status).toLowerCase();
  if (["succeeded", "completed", "already_absent"].includes(status)) {
    return renderResultState(action);
  }
  if (!personalizationMutationPayloadReady(action)) {
    const label =
      stringValue(actionPresentation(action).label) ||
      stringValue(action.data.label) ||
      {
        preference_update: "Update preference",
        preference_forget: "Forget preference",
        preference_opt_out: "Turn off personalization"
      }[action.type] ||
      "Change personalization";
    return `
      <div class="apw-action-link apw-personalization-action" data-disabled="true" aria-disabled="true" ${actionDataAttributes(action, "")}>
        <strong>${escapeHtml(label)}</strong>
        <small>This change needs specific authorized preference details before it can be submitted.</small>
      </div>
    `;
  }
  return renderPromptAction(action, true, true);
}

export function personalizationMutationPayloadReady(action: WidgetAction): boolean {
  if (action.type === "preference_opt_out") {
    return true;
  }
  const executionPayload = parseRecord(actionExecution(action).payload);
  const category = stringValue(action.data.category) || stringValue(action.data.dimension) || stringValue(executionPayload.category);
  const value = stringValue(action.data.value) || stringValue(executionPayload.value);
  const message = stringValue(action.data.message) || stringValue(executionPayload.message);
  if (action.type === "preference_update") {
    return Boolean((category && value) || message);
  }
  const preferenceId =
    stringValue(action.data.preferenceId) ||
    stringValue(action.data.preference_id) ||
    stringValue(executionPayload.preferenceId) ||
    stringValue(executionPayload.preference_id);
  const forgetAll =
    action.data.forgetAll === true ||
    action.data.forget_all === true ||
    executionPayload.forgetAll === true ||
    executionPayload.forget_all === true ||
    stringValue(action.data.scope).toLowerCase() === "all";
  return Boolean(preferenceId || category || forgetAll);
}

export function renderQuickReply(action: WidgetAction): string {
  return renderPromptAction(action);
}

export function renderContactSupport(action: WidgetAction): string {
  const execution = actionExecution(action);
  const url = stringValue(execution.url) || stringValue(action.data.url) || stringValue(action.data.fallbackUrl);
  if (safeUrl(url)) {
    return renderActionLink(action, "apw-contact-support-action");
  }
  const prompt = stringValue(execution.prompt) || stringValue(action.data.prompt);
  if (prompt) {
    return renderQuickReply(action);
  }
  return renderResultState(action);
}

export function actionPresentation(action: WidgetAction): Record<string, unknown> {
  return parseRecord(action.presentation);
}

export function actionExecution(action: WidgetAction): Record<string, unknown> {
  return parseRecord(action.execution);
}

export function renderTrustBadge(action: WidgetAction): string {
  const trust = parseRecord(action.trust);
  const source = parseRecord(action.source);
  const confidence = numberValue(trust.confidence);
  const sourceCount = numberValue(trust.sourceCount);
  const adapter = stringValue(source.adapter) || stringValue(source.toolName);
  const parts = [
    confidence === null ? "" : `${Math.round(confidence * 100)}%`,
    sourceCount === null || sourceCount <= 0 ? "" : `${sourceCount} src`,
    adapter
  ].filter(Boolean);
  if (!parts.length) {
    return "";
  }
  return `<span class="apw-action-trust">${escapeHtml(parts.join(" / "))}</span>`;
}

export function renderPriceStack(data: Record<string, unknown>): string {
  const currency = stringValue(data.currency) || "USD";
  const price = formatPrice(data.price, currency);
  const compareAt = formatPrice(data.compareAtPrice ?? data.compare_at_price, currency);
  if (!price && !compareAt) {
    return "";
  }
  return `
    <div class="apw-price-stack">
      ${price ? `<b>${escapeHtml(price)}</b>` : ""}
      ${compareAt && compareAt !== price ? `<s>${escapeHtml(compareAt)}</s>` : ""}
    </div>
  `;
}

export function renderProductPriceRange(action: WidgetAction, items: Array<Record<string, unknown>>): string {
  if (!booleanValue(action.data.priceRangeEnabled ?? action.data.price_range_enabled, true)) {
    return "";
  }
  const prices = items
    .map((item) => numberValue(item.price))
    .filter((price): price is number => price !== null && price > 0);
  if (prices.length < 2) {
    return "";
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return "";
  }
  const currency = stringValue(items.find((item) => stringValue(item.currency))?.currency) || "USD";
  const minLabel = formatPrice(min, currency);
  const maxLabel = formatPrice(max, currency);
  return `
    <div class="apw-price-range" aria-label="Visible result price range" data-grid-id="${escapeAttribute(action.id)}" data-min="${escapeAttribute(String(min))}" data-max="${escapeAttribute(String(max))}" data-currency="${escapeAttribute(currency)}" style="--apw-price-left:0%;--apw-price-right:0%;">
      <div><span>Price range</span><b class="apw-price-range-label">${escapeHtml([minLabel, maxLabel].filter(Boolean).join(" - "))}</b></div>
      <div class="apw-price-slider">
        <span class="apw-price-rail" aria-hidden="true"><i></i></span>
        <input class="apw-price-input apw-price-min" type="range" min="${escapeAttribute(String(min))}" max="${escapeAttribute(String(max))}" step="0.01" value="${escapeAttribute(String(min))}" aria-label="Minimum price">
        <input class="apw-price-input apw-price-max" type="range" min="${escapeAttribute(String(min))}" max="${escapeAttribute(String(max))}" step="0.01" value="${escapeAttribute(String(max))}" aria-label="Maximum price">
      </div>
    </div>
  `;
}

export function saleBadge(data: Record<string, unknown>): string {
  const explicit = stringValue(data.saleBadge) || stringValue(data.sale_label) || stringValue(data.saleLabel);
  if (explicit) {
    return explicit;
  }
  const discount = numberValue(data.discountPercent ?? data.discount_percent);
  return discount !== null && discount > 0 ? `-${Math.round(discount)}%` : "";
}

export function actionForItem(
  parent: WidgetAction,
  type: string,
  id: string,
  data: Record<string, unknown>
): WidgetAction {
  return {
    type,
    id,
    data,
    source: parent.source,
    trust: parent.trust,
    presentation: {
      ...parseRecord(parent.presentation),
      label: stringValue(data.label) || stringValue(parent.presentation?.label)
    },
    execution: type === "quick_reply" || type === "try_on"
      ? { mode: "prompt", prompt: stringValue(data.prompt), enabled: Boolean(stringValue(data.prompt)) }
      : {
          mode: safeUrl(stringValue(data.url)) ? "link" : "display",
          url: stringValue(data.url),
          enabled: data.enabled !== false,
          confirmationRequired: data.requiresConfirmation === true,
          confirmation: stringValue(data.confirmation),
          destinationKind: stringValue(data.destinationKind)
        }
  };
}

export function actionDataAttributes(action: WidgetAction, url: string, itemId?: string): string {
  const execution = actionExecution(action);
  const destinationKind = stringValue(execution.destinationKind) || stringValue(action.data.destinationKind);
  const sku = stringValue(action.data.sku) || itemId || "";
  const variantId = stringValue(action.data.shopifyVariantId) || stringValue(action.data.shopify_variant_id);
  const quantity = numberValue(action.data.quantity) ?? 1;
  const currency = stringValue(action.data.currency);
  const valueMicros = actionValueMicros(action.data.price, quantity);
  const attribution = {
    ...parseRecord(execution.attribution),
    ...parseRecord(action.data.attribution)
  };
  const pairs: Array<[string, string]> = [
    ["data-action-id", itemId || action.id],
    ["data-action-type", action.type]
  ];
  if (destinationKind) {
    pairs.push(["data-destination-kind", destinationKind]);
  }
  if (url) {
    pairs.push(["data-url", url]);
  }
  if (sku) {
    pairs.push(["data-sku", sku]);
  }
  if (variantId) {
    pairs.push(["data-shopify-variant-id", variantId]);
  }
  if (Number.isFinite(quantity) && quantity > 0) {
    pairs.push(["data-quantity", String(quantity)]);
  }
  if (currency) {
    pairs.push(["data-currency", currency]);
  }
  if (valueMicros > 0) {
    pairs.push(["data-value-micros", String(valueMicros)]);
  }
  if (Object.keys(attribution).length) {
    pairs.push(["data-attribution", JSON.stringify(attribution)]);
  }
  if (["preference_update", "preference_forget", "preference_opt_out"].includes(action.type)) {
    const payload = parseRecord(execution.payload);
    const preferenceMessage = boundedActionAttribute(
      stringValue(action.data.message) || stringValue(payload.message),
      1000
    );
    const preferenceCategory = boundedActionAttribute(
      stringValue(action.data.category) || stringValue(action.data.dimension) || stringValue(payload.category),
      80
    );
    const preferenceValue = boundedActionAttribute(
      stringValue(action.data.value) || stringValue(payload.value),
      160
    );
    const preferenceId = boundedActionAttribute(
      stringValue(action.data.preferenceId) ||
        stringValue(action.data.preference_id) ||
        stringValue(payload.preferenceId) ||
        stringValue(payload.preference_id),
      80
    );
    const forgetAll =
      action.data.forgetAll === true ||
      action.data.forget_all === true ||
      payload.forgetAll === true ||
      payload.forget_all === true ||
      stringValue(action.data.scope).toLowerCase() === "all";
    if (preferenceMessage) pairs.push(["data-preference-message", preferenceMessage]);
    if (preferenceCategory) pairs.push(["data-preference-category", preferenceCategory]);
    if (preferenceValue) pairs.push(["data-preference-value", preferenceValue]);
    if (preferenceId) pairs.push(["data-preference-id", preferenceId]);
    if (forgetAll) pairs.push(["data-preference-forget-all", "true"]);
  }
  return pairs.map(([key, value]) => `${key}="${escapeAttribute(value)}"`).join(" ");
}

export function actionElementMetadata(element: HTMLElement): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  const sku = element.getAttribute("data-sku") || "";
  const variantId = element.getAttribute("data-shopify-variant-id") || "";
  const quantity = numberAttribute(element, "data-quantity");
  const attribution = parseJsonAttribute(element.getAttribute("data-attribution") || "");
  if (sku) {
    metadata.sku = sku;
  }
  if (variantId) {
    metadata.shopify_variant_id = variantId;
  }
  if (quantity !== null) {
    metadata.quantity = quantity;
  }
  if (attribution) {
    metadata.attribution = attribution;
  }
  return metadata;
}

export function renderSources(sources?: WidgetSource[]): string {
  const usableSources = normalizeSources(sources).slice(0, 4);
  if (!usableSources.length) {
    return "";
  }
  return `
    <div class="apw-sources" aria-label="Sources">
      ${usableSources
        .map((source, index) => {
          const title = source.title || `Source ${index + 1}`;
          const snippet = source.snippet ? `<small>${escapeHtml(source.snippet)}</small>` : "";
          if (source.url && safeUrl(source.url)) {
            return `<a class="apw-source" href="${escapeAttribute(source.url)}" target="_blank" rel="noreferrer"><strong>${escapeHtml(title)}</strong>${snippet}</a>`;
          }
          return `<span class="apw-source"><strong>${escapeHtml(title)}</strong>${snippet}</span>`;
        })
        .join("")}
    </div>
  `;
}

export function sourcesFromToolContext(toolContext?: Record<string, unknown>): WidgetSource[] {
  const rawSources = toolContext?.sources;
  return normalizeSources(Array.isArray(rawSources) ? rawSources : []);
}

export function normalizeActions(value: unknown): WidgetAction[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const actions: WidgetAction[] = [];
  for (const action of value) {
    if (!action || typeof action !== "object" || Array.isArray(action)) {
      continue;
    }
    const record = action as Record<string, unknown>;
    const type = stringValue(record.type);
    const id = stringValue(record.id) || type;
    const data = record.data && typeof record.data === "object" && !Array.isArray(record.data) ? (record.data as Record<string, unknown>) : {};
    const source = parseRecord(record.source);
    const trust = parseRecord(record.trust);
    const presentation = parseRecord(record.presentation);
    const execution = parseRecord(record.execution);
    if (!type || !id) {
      continue;
    }
    actions.push({
      type,
      id,
      ...(stringValue(record.label) ? { label: stringValue(record.label) } : {}),
      data,
      ...(Object.keys(source).length ? { source } : {}),
      ...(Object.keys(trust).length ? { trust } : {}),
      ...(Object.keys(presentation).length ? { presentation } : {}),
      ...(Object.keys(execution).length ? { execution } : {})
    });
  }
  return actions;
}

export function normalizeSources(value: unknown): WidgetSource[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const sources: WidgetSource[] = [];
  for (const source of value) {
    if (!source || typeof source !== "object") {
      continue;
    }
    const record = source as Record<string, unknown>;
    const title = publicVisitorText(
      stringValue(record.title) || stringValue(record.name) || stringValue(record.document_title)
    );
    if (!title) {
      continue;
    }
    const snippet = publicVisitorText(
      stringValue(record.snippet) || stringValue(record.text) || stringValue(record.summary)
    );
    const url = stringValue(record.url) || stringValue(record.href);
    sources.push({
      title,
      snippet: snippet || undefined,
      url: url && safeUrl(url) ? url : undefined
    });
  }
  return sources;
}

export function actionValueMicros(price: unknown, quantity: number): number {
  const amount = numberValue(price);
  if (amount === null || amount <= 0 || !Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }
  return Math.round(amount * quantity * 1_000_000);
}

export function safeActionUrl(action: WidgetAction, value: string): boolean {
  if (!safeUrl(value)) {
    return false;
  }
  if (!storefrontBoundActionTypes.has(action.type)) {
    return true;
  }
  try {
    const destination = new URL(value);
    const storefront = new URL(window.location.href);
    return ["http:", "https:"].includes(storefront.protocol) && destination.origin === storefront.origin;
  } catch {
    return false;
  }
}

export const sonicFashionActionTypes = new Set<string>([
  "result_state",
  "product_grid",
  "product_card",
  "product_detail_link",
  "refinement_chip",
  "remove_refinement",
  "clear_refinements",
  "price_range",
  "view_all",
  "comparison",
  "comparison_page",
  "recommendation",
  "add_to_cart",
  "cart_result",
  "view_cart",
  "continue_shopping",
  "checkout",
  "try_on",
  "try_on_status",
  "try_on_result",
  "policy_source",
  "inventory_state",
  "stale_state",
  "order_status",
  "sign_in",
  "support_handoff",
  "weather_card",
  "preferences_summary",
  "preference_update",
  "preference_forget",
  "preference_opt_out",
  "retry",
  "switch_to_text",
  "contact_support"
]);

export const sonicFashionLegacyActionTypes = new Set<string>([
  "comparison_carousel",
  "handoff_status",
  "citation_list",
  "voice_status",
  "quick_reply",
  "weather_context"
]);

export const sonicFashionPreservedActionTypes = new Set<string>([
  "result_state",
  "cart_result",
  "inventory_state",
  "stale_state",
  "order_status",
  "sign_in",
  "support_handoff",
  "handoff_status",
  "try_on_status",
  "try_on_result",
  "preferences_summary",
  "retry",
  "switch_to_text",
  "contact_support"
]);

export const storefrontBoundActionTypes = new Set<string>([
  "add_to_cart",
  "checkout",
  "view_cart",
  "continue_shopping",
  "sign_in"
]);
